import React, { useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef } from "react";
import { fmtDateTime } from "@/lib/datetime";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSheetDrag } from "@/components/motion/useSheetDrag";
import { GuestPopup } from "@/pages/GuestsPage";
import BillingPartySection, { EMPTY_COMPANY, saveCompany } from "@/components/BillingPartySection";
import { CaretLeft, CaretRight, CaretDown, Plus, X, CircleNotch, Warning, Copy, ArrowSquareOut, CheckCircle, PaperPlaneTilt, List, Receipt, Envelope, Trash, Bell, PencilSimple, User, Note, PushPin, Bed, Lock, ChatCircleText, FileText, Check, MagnifyingGlassMinus, MagnifyingGlassPlus, IdentificationCard, Tag } from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import InvoicesTab from "@/components/InvoicesTab";
import ResponsiveSheet from "@/components/ResponsiveSheet";
import { DatePicker } from "@/components/ui/date-picker";
import { useProperty } from "@/contexts/PropertyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

// ---- helpers ---------------------------------------------------------------
const DAY_MS = 24 * 60 * 60 * 1000;
// Format a Date as YYYY-MM-DD using LOCAL date components (not UTC!).
// Using toISOString() shifts by timezone offset — e.g. Zagreb midnight → previous day UTC.
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const parse = (s) => new Date(s + "T00:00:00");
// Calendar-day arithmetic (DST-safe — adding 24h on 25.10. would land on 25.10. 23:00)
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const daysBetween = (a, b) => Math.round((b - a) / DAY_MS);
const nightsBetween = (a, b) => (a && b ? daysBetween(parse(a), parse(b)) : 0);
const OTA_SOURCES = new Set(["booking_com", "airbnb", "expedia", "agoda", "vrbo", "hostelworld", "hotelbeds", "channex"]);

const CAL_LOOKBACK = 30;
const CAL_VISIBLE_DAYS = 31;
const CAL_AHEAD_OPTIONS = [
  { n: 30, label: "Mjesec" },
  { n: 60, label: "2 mjeseca" },
  { n: 182, label: "6 mjeseci" },
  { n: 365, label: "Godina" },
];

function generateDays(start, count) {
  const out = [];
  for (let i = 0; i < count; i++) out.push(addDays(start, i));
  return out;
}

export const STATUS_LABELS = {
  confirmed: "Potvrđeno",
  checked_in: "Boravak",
  checked_out: "Odjava",
  tentative: "Tentativna",
  cancelled: "Otkazano",
  no_show: "No-show",
};

export const SOURCE_LABELS = {
  direct: "Direktna",
  booking_engine: "Booking Engine",
  walk_in: "Walk-in",
  phone: "Telefon",
  booking_com: "Booking.com",
  booking: "Booking.com",
  airbnb: "Airbnb",
  expedia: "Expedia",
  vrbo: "VRBO",
  agoda: "Agoda",
  hotelbeds: "HotelBeds",
  hostelworld: "Hostelworld",
  channex: "Ostalo",  // legacy — will be phased out
  pitchup: "Pitchup",
  manual_block: "Blokada",
  other: "Ostalo",
};

// Group reservation sources into the palette buckets defined in Tema.
// Each PMS source value maps to one of six palette keys used by the calendar
// to derive its color from `tenant.source_colors`.
export const SOURCE_PALETTE_KEY = {
  direct: "direct",
  walk_in: "direct",
  phone: "direct",
  booking_engine: "booking_engine",
  booking_com: "booking",
  booking: "booking",
  airbnb: "airbnb",
  expedia: "expedia",
  channex: "other",
  pitchup: "other",
  other: "other",
};

export const DEFAULT_SOURCE_COLORS = {
  booking: "#003580",
  airbnb: "#FF385C",
  expedia: "#FFC72C",
  direct: "#0E9F6E",
  booking_engine: "#4F46E5",
  other: "#6B7280",
};

/** Returns the background color to use for a reservation block in the calendar. */
export function reservationColor(source, tenantSourceColors) {
  const paletteKey = SOURCE_PALETTE_KEY[source] || "other";
  const colors = tenantSourceColors || {};
  return colors[paletteKey] || DEFAULT_SOURCE_COLORS[paletteKey] || DEFAULT_SOURCE_COLORS.other;
}

const CHECKIN_STATUS = {
  pending:    { label: "Čeka prijavu",   tone: "amber",  desc: "Gost još nije ispunio check-in formu." },
  submitted:  { label: "Forma ispunjena", tone: "blue",  desc: "Gost je popunio formu — može se napraviti single-click check-in." },
  checked_in: { label: "Check-in poslan", tone: "green", desc: "Podaci su poslani na eVisitor." },
  cancelled:  { label: "Otkazano",       tone: "red",   desc: "Link je deaktiviran." },
  expired:    { label: "Isteklo",        tone: "gray",  desc: "Boravak završio — link više nije aktivan." },
};

// =============================================================================
// Check-in section inside the reservation drawer
// =============================================================================
function CheckinSection({ reservationId, initialStatus, onUpdated }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [acting, setActing] = useState(false);
  const [actionMsg, setActionMsg] = useState("");

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/reservations/${reservationId}/checkin-link`).then((r) => {
      if (!cancel) setInfo(r.data);
    }).catch(() => {}).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [reservationId]);

  const status = info?.checkin_status || initialStatus || "pending";
  const meta = CHECKIN_STATUS[status] || CHECKIN_STATUS.pending;
  const fullUrl = useMemo(() => {
    if (!info?.path) return "";
    return `${window.location.origin}${info.path}`;
  }, [info]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = fullUrl;
      document.body.appendChild(ta);
      ta.select(); document.execCommand("copy"); ta.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const singleClickCheckIn = async () => {
    if (!window.confirm("Potvrđujete da je gost stigao u apartman i da se podaci šalju na eVisitor?")) return;
    setActing(true);
    setActionMsg("");
    try {
      const { data } = await api.post(`/reservations/${reservationId}/checkin`, {});
      if (data.ok) {
        setActionMsg(`✓ Uspješno (${data.submitted}/${data.total} gostiju poslano).`);
        setInfo((p) => p ? { ...p, checkin_status: "checked_in" } : p);
        onUpdated && onUpdated({ checkin_status: "checked_in", status: "checked_in" });
      } else {
        setActionMsg(`Djelomično: ${data.submitted}/${data.total} poslano. Pogledajte detalje u 'eVisitor prijave'.`);
        setInfo((p) => p ? { ...p, checkin_status: "submitted" } : p);
      }
    } catch (e) {
      setActionMsg(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const toneCls = {
    amber: "bg-amber-50 text-amber-800 border-amber-300",
    blue:  "bg-blue-50 text-blue-800 border-blue-300",
    green: "bg-emerald-50 text-emerald-800 border-emerald-300",
    red:   "bg-rose-50 text-rose-800 border-rose-300",
    gray:  "bg-slate-100 text-slate-700 border-slate-300",
  }[meta.tone];

  const linkDisabled = status === "cancelled" || status === "expired";

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3" data-testid="drawer-checkin-section">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Check-in</span>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${toneCls}`} data-testid="checkin-status-badge">{meta.label}</span>
      </div>
      <p className="mb-2.5 text-[11px] text-muted-foreground">{meta.desc}</p>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><CircleNotch size={14} className="animate-spin" /> Učitavam…</div>
      ) : (
        <>
          <div className="mb-2 flex gap-2">
            <input
              readOnly
              value={fullUrl}
              className="rw-input flex-1 !py-1.5 text-xs"
              data-testid="checkin-link-input"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={copyLink}
              disabled={linkDisabled || !fullUrl}
              className="flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-40"
              data-testid="checkin-copy-btn"
              title="Kopiraj link"
            >
              {copied ? <><CheckCircle size={14} weight="fill" className="text-emerald-500" /> Kopirano</> : <><Copy size={14} /> Kopiraj</>}
            </button>
            <a
              href={linkDisabled ? undefined : fullUrl}
              target="_blank"
              rel="noreferrer"
              className={`flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-muted ${linkDisabled ? "pointer-events-none opacity-40" : ""}`}
              data-testid="checkin-open-btn"
              title="Otvori formu u novoj kartici"
            >
              <ArrowSquareOut size={14} />
            </a>
          </div>

          {status === "checked_in" ? (
            <div className="flex items-center justify-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800" data-testid="checkin-done-badge">
              <CheckCircle size={14} weight="fill" /> Prijavljeno na eVisitor
            </div>
          ) : (
            <button
              type="button"
              onClick={singleClickCheckIn}
              disabled={acting || status !== "submitted"}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              data-testid="single-click-checkin"
            >
              {acting ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />}
              {status === "submitted" ? "Single-click check-in (pošalji na eVisitor)" : "Čeka da gost ispuni formu"}
            </button>
          )}
          {["submitted", "checked_in", "expired"].includes(status) && (
            <Link
              to={`/check-in?reservation=${reservationId}`}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground hover:bg-muted"
              data-testid="checkin-view-registration"
            >
              <IdentificationCard size={14} /> Pogledaj eVisitor prijavu
            </Link>
          )}
          {actionMsg && <p className="mt-2 text-[11px] text-muted-foreground" data-testid="checkin-action-msg">{actionMsg}</p>}
        </>
      )}
    </div>
  );
}

// ---- Reservation slide-over -------------------------------------------------
function TemplateDeliveryLog({ reservationId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let alive = true;
    api.get(`/message-templates/delivery-log?reservation_id=${reservationId}&limit=20`)
      .then((r) => { if (alive) setLogs(r.data.items || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reservationId, reloadKey]);
  const reload = () => setReloadKey((k) => k + 1);

  if (loading) return <div className="rw-skeleton h-24 rounded-md" />;
  if (logs.length === 0) return (
    <div data-testid="delivery-log-empty">
      <p className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">Ovoj rezervaciji još nije poslana nijedna automatska poruka.</p>
      <TemplateDiagnose reservationId={reservationId} onSent={reload} />
    </div>
  );

  const STATUS_TONE = {
    sent:    "text-emerald-700 bg-emerald-50 border-emerald-200",
    failed:  "text-rose-700 bg-rose-50 border-rose-200",
    skipped: "text-slate-600 bg-slate-100 border-slate-200",
  };
  const STATUS_LBL = { sent: "poslano", failed: "greška", skipped: "preskočeno", dismissed: "ručno preskočeno" };

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3" data-testid="drawer-template-log">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Automatske poruke</p>
      <ul className="space-y-1.5">
        {logs.map((l) => (
          <li key={l.id} className="flex items-start justify-between gap-2 text-xs">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-foreground">
                {l.template_name || "?"}
                {l.is_test && <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-bold text-amber-800">TEST</span>}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {fmtDateTime(l.sent_at)}
                {l.language_used && ` · ${l.language_used}`}
                {l.error && ` · ${l.error.slice(0, 60)}`}
              </p>
            </div>
            <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_TONE[l.status] || STATUS_TONE.skipped}`}>
              {STATUS_LBL[l.status] || l.status}
            </span>
          </li>
        ))}
      </ul>
      <TemplateDiagnose reservationId={reservationId} onSent={reload} />
    </div>
  );
}

const NAME_PHONE = typeof window !== "undefined" && window.innerWidth < 640;
const NAME_FS = NAME_PHONE ? 10 : 11;
let _measureCtx = null;
function textWidth(txt) {
  if (!_measureCtx) {
    _measureCtx = document.createElement("canvas").getContext("2d");
    _measureCtx.font = `600 ${NAME_FS}px ${getComputedStyle(document.body).fontFamily}`;
  }
  return _measureCtx.measureText(txt).width;
}

// One line "Ime Prezime" only when it fits whole; otherwise ime on line 1 and
// prezime on line 2, each clipped to what fits (no "I." abbreviations).
function StayName({ name, width }) {
  const full = (name || "").trim();
  const parts = full.split(/\s+/);
  const first = parts[0] || "";
  const last = parts.slice(1).join(" ");
  const avail = width - (NAME_PHONE ? 8 : 12);
  const style = { fontSize: NAME_FS };
  if (!last || textWidth(full) <= avail) {
    return <span className="min-w-0 overflow-hidden whitespace-nowrap px-1 font-normal leading-tight sm:px-1.5" style={style} data-testid="cal-res-short-name">{full}</span>;
  }
  return (
    <span className="flex min-w-0 flex-col justify-center overflow-hidden whitespace-nowrap px-1 font-normal leading-[1.15] sm:px-1.5" style={style} data-testid="cal-res-short-name">
      <span className="overflow-hidden">{first}</span>
      <span className="overflow-hidden">{last}</span>
    </span>
  );
}

const DIAG_TONE = {
  sent: "border-emerald-200 bg-emerald-50 text-emerald-800", due: "border-sky-200 bg-sky-50 text-sky-800",
  waiting: "border-slate-200 bg-slate-50 text-slate-700", dismissed: "border-slate-200 bg-slate-50 text-slate-700",
  blocked: "border-amber-200 bg-amber-50 text-amber-800", failed: "border-rose-200 bg-rose-50 text-rose-800",
};

export function TemplateDiagnose({ reservationId, onSent, autoLoad = false }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const sendNow = async (it) => {
    if (!window.confirm(`Poslati poruku "${it.name}" gostu sada?`)) return;
    setSendingId(it.template_id);
    try {
      const { data } = await api.post("/message-templates/run-scheduler-now", { send: [{ template_id: it.template_id, reservation_id: reservationId }], dismiss: [] }, { params: { dry_run: false } });
      const r = (data.results || [])[0];
      if (r?.status === "sent") toast.success("Poruka poslana"); else toast.error(r?.error || "Poruka nije poslana");
      onSent?.();
      run();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSendingId(null); }
  };
  useEffect(() => { if (autoLoad) run(); }, [autoLoad]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/message-templates/diagnose/${reservationId}`); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2" data-testid="template-diagnose">
      <button type="button" onClick={run} disabled={busy} className="text-xs font-medium text-primary hover:underline disabled:opacity-50" data-testid="template-diagnose-btn">
        {busy ? "Provjeravam…" : "Zašto (nije) poslano?"}
      </button>
      {items && items.length === 0 && <p className="mt-1 text-xs text-muted-foreground">Nema aktivnih predložaka.</p>}
      {items && items.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid="template-diagnose-list">
          {items.map((it) => (
            <li key={it.template_id} className={`rounded-md border px-2 py-1.5 text-xs ${DIAG_TONE[it.state] || DIAG_TONE.waiting}`} data-testid={`template-diagnose-${it.template_id}`}>
              <p className="font-semibold">{it.name} <span className="font-normal opacity-70">· {it.trigger}</span></p>
              <p>{it.reason}</p>
              {it.notes.map((n, i) => <p key={i} className="text-[11px] opacity-80">• {n}</p>)}
              {it.state !== "sent" && (
                <button type="button" onClick={() => sendNow(it)} disabled={sendingId === it.template_id} className="mt-1 inline-flex items-center gap-1 rounded border border-current/30 bg-white/70 px-2 py-0.5 text-[11px] font-semibold hover:bg-white disabled:opacity-50" data-testid={`template-send-now-${it.template_id}`}>
                  {sendingId === it.template_id ? <CircleNotch size={11} className="animate-spin" /> : <PaperPlaneTilt size={11} weight="fill" />} Pošalji sada
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// =============================================================================
// Guest stats card — inline in the reservation drawer main tab
// =============================================================================
function GuestStatsSection({ guestId, currentReservationId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!guestId) { setLoading(false); return; }
    let cancel = false;
    setLoading(true);
    api.get(`/guests/${guestId}/statistics`).then((r) => {
      if (!cancel) setData(r.data);
    }).catch((e) => {
      if (!cancel) setError(formatApiError(e));
    }).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [guestId]);

  if (!guestId) return null;
  if (loading) return (
    <div className="rounded-lg border border-border p-3" data-testid="res-guest-stats-loading">
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><CircleNotch size={14} className="animate-spin" /> Učitavam statistiku gosta...</div>
    </div>
  );
  if (error) return (
    <div className="rounded-lg border border-border p-3 text-xs text-muted-foreground" data-testid="res-guest-stats-error">Statistika nije dostupna.</div>
  );
  if (!data) return null;

  const others = (data.reservations || []).filter((r) => r.id !== currentReservationId);
  return (
    <div className="rounded-lg border border-border" data-testid="res-guest-stats">
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <User size={14} weight="bold" /> Statistika gosta
        </div>
        {data.total_stays >= 3 && (
          <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-600" data-testid="res-guest-vip-badge">VIP • Povratnik</span>
        )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 divide-x divide-border">
        <div className="px-3 py-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Boravaka</p>
          <p className="mt-1 text-2xl font-bold text-foreground" data-testid="res-guest-stat-stays">{data.total_stays}</p>
        </div>
        <div className="px-3 py-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Noći (Ø)</p>
          <p className="mt-1 text-2xl font-bold text-foreground" data-testid="res-guest-stat-nights">{data.total_nights}</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">prosjek {data.avg_nights}</p>
        </div>
        <div className="px-3 py-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Potrošeno</p>
          <p className="mt-1 text-2xl font-bold text-foreground" data-testid="res-guest-stat-spent">€{data.total_spent.toFixed(0)}</p>
        </div>
      </div>

      {/* Past reservations list */}
      {others.length > 0 && (
        <div className="border-t border-border">
          <p className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Prošli boravci ({others.length})</p>
          <ul className="max-h-40 divide-y divide-border overflow-y-auto text-xs">
            {others.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-1.5" data-testid={`res-guest-past-${r.code}`}>
                <span className="font-mono text-[10px] text-muted-foreground">{r.code}</span>
                <span className="flex-1 truncate text-foreground">{r.check_in} → {r.check_out}</span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{r.nights}n</span>
                <span className="font-semibold text-foreground">€{(r.total_amount || 0).toFixed(0)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {others.length === 0 && data.total_stays <= 1 && (
        <p className="border-t border-border px-3 py-2 text-xs italic text-muted-foreground">Ovo je prvi boravak ovog gosta.</p>
      )}
    </div>
  );
}

// =============================================================================
// Reservation Modal (Rentlio-style centered popup with sidebar tabs)
// =============================================================================
function NavIconButton({ Icon, label, active, onClick, id }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-10 w-10 items-center justify-center rounded-md transition sm:h-11 sm:w-11 ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
      title={label}
      data-testid={`res-tab-${id}`}
    >
      <Icon size={18} weight={active ? "fill" : "regular"} className="sm:hidden" />
      <Icon size={20} weight={active ? "fill" : "regular"} className="hidden sm:block" />
    </button>
  );
}

export function ReservationDrawer({ reservation, units, properties, onClose, onSaved, onDeleted, initialDates, onMoveConflict }) {
  const isNew = !reservation;
  const [chatFor, setChatFor] = useState(null);
  const [guestProfileId, setGuestProfileId] = useState(null);
  const [activeTab, setActiveTab] = useState("main"); // main | invoices | messages
  const [form, setForm] = useState(() => {
    if (reservation) return { ...reservation, tasks: reservation.tasks || [] };
    return {
      property_id: initialDates?.property_id || properties[0]?.id || "",
      unit_id: initialDates?.unit_id || units.find((u) => u.active !== false)?.id || "",
      source: "direct",
      check_in: initialDates?.check_in || fmt(new Date()),
      check_out: initialDates?.check_out || fmt(addDays(new Date(), 1)),
      adults: 2,
      children: 0,
      primary_guest_name: "",
      primary_guest_email: "",
      primary_guest_phone: "",
      status: "confirmed",
      payment_status: "unpaid",
      base_amount: 0,
      extras_amount: 0,
      discount_amount: 0,
      total_amount: 0,
      notes: "",
      tasks: [],
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editingNote, setEditingNote] = useState(() => !((reservation?.notes || "").trim()));
  const [savedNote, setSavedNote] = useState(reservation?.notes || "");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDue, setNewTaskDue] = useState("");
  // Auto-price direct reservations from the calendar rates (until the owner types a total manually).
  const [priceTouched, setPriceTouched] = useState(false);
  const autoPrice = isNew || (form.source === "direct" && !Number(reservation?.total_amount));
  useEffect(() => {
    if (!autoPrice || priceTouched || !form.unit_id || !form.check_in || !form.check_out || form.check_out <= form.check_in) return;
    let cancelled = false;
    api.get(`/units/${form.unit_id}/price-preview`, { params: { check_in: form.check_in, check_out: form.check_out, channel: "direct" } })
      .then(({ data }) => {
        if (cancelled || data?.total_price == null) return;
        setForm((f) => {
          const base = Number(data.total_price) || 0;
          const total = Math.max(0, base + (Number(f.extras_amount) || 0) - (Number(f.discount_amount) || 0));
          return { ...f, base_amount: base, total_amount: Math.round(total * 100) / 100 };
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [autoPrice, priceTouched, form.unit_id, form.check_in, form.check_out]); // eslint-disable-line react-hooks/exhaustive-deps
  // Guest address details — loaded from /guests/{primary_guest_id} on open; used by invoices + eVisitor
  const [guestAddress, setGuestAddress] = useState({
    address: "", city: "", postal_code: "", country: "", oib: "", email: "", phone: "",
  });
  const guestAddressLoadedRef = useRef(guestAddress);

  useEffect(() => {
    (async () => {
      const gid = reservation?.primary_guest_id;
      if (!gid) return;
      try {
        const { data } = await api.get(`/guests/${gid}`);
        const loaded = {
          address: data?.address || "",
          city: data?.city || "",
          postal_code: data?.postal_code || "",
          country: data?.country || data?.country_of_residence || data?.nationality || "",
          oib: data?.oib || "",
          email: data?.email || "",
          phone: data?.phone || "",
        };
        setGuestAddress(loaded);
        guestAddressLoadedRef.current = loaded;
      } catch { /* non-fatal */ }
    })();
  }, [reservation?.primary_guest_id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Billing party: person (primary guest) or company (guests.is_company) — invoice buyer.
  const [billingMode, setBillingMode] = useState(reservation?.billing_company_id ? "company" : "person");
  const [company, setCompany] = useState({ ...EMPTY_COMPANY });
  useEffect(() => {
    const cid = reservation?.billing_company_id;
    if (!cid) return;
    api.get(`/guests/${cid}`).then(({ data }) => setCompany({ id: data.id, ...Object.fromEntries(Object.keys(EMPTY_COMPANY).filter((k) => k !== "id").map((k) => [k, data[k] || ""])) })).catch(() => {});
  }, [reservation?.billing_company_id]);

  // When unit changes, default property. Price auto-fill removed (F6.11) —
  // prices now come from explicit RatePeriods only; admin types total manually.
  useEffect(() => {
    if (form.unit_id) {
      const u = units.find((x) => x.id === form.unit_id);
      if (u && u.property_id !== form.property_id) {
        setForm((f) => ({ ...f, property_id: u.property_id }));
      }
    }
  }, [form.unit_id, units]);

  // Legal max nightly price of the unit's room type (manual prices may not exceed it).
  const [maxPrice, setMaxPrice] = useState(null);
  useEffect(() => {
    if (!form.unit_id) { setMaxPrice(null); return; }
    api.get(`/units/${form.unit_id}/max-price`).then(({ data }) => setMaxPrice(data?.max_price ?? null)).catch(() => setMaxPrice(null));
  }, [form.unit_id]);
  const maxPriceExceeded = !!maxPrice && !OTA_SOURCES.has(form.source) && !reservation?.is_block
    && (isNew || ["total_amount", "extras_amount", "check_in", "check_out", "unit_id"].some((k) => form[k] !== reservation?.[k]))
    && nightsBetween(form.check_in, form.check_out) > 0
    && ((Number(form.total_amount) || 0) - (Number(form.extras_amount) || 0)) / nightsBetween(form.check_in, form.check_out) > maxPrice + 0.005;

  const submit = async (payloadOverride = null) => {
    setError("");
    const payload = payloadOverride || { ...form };
    if (maxPriceExceeded && !payloadOverride) {
      const msg = `Cijena po noćenju prelazi max cijenu (€${Number(maxPrice).toFixed(2)}) za ovaj tip smještaja.`;
      setError(msg); toast.error(msg);
      return null;
    }
    if (!payloadOverride && billingMode === "company") {
      if (!company.company_name.trim() || !company.company_oib.trim()) {
        setError("Za tvrtku su obavezni naziv i OIB"); toast.error("Za tvrtku su obavezni naziv i OIB");
        return null;
      }
      try {
        const cid = await saveCompany(company);
        setCompany((c) => ({ ...c, id: cid }));
        payload.billing_company_id = cid;
      } catch (e) { toast.error(formatApiError(e)); return null; }
      if (!payload.primary_guest_name?.trim()) payload.primary_guest_name = (payload.invoice_guest_name || "").trim() || company.company_name;
    } else if (!payloadOverride) {
      payload.billing_company_id = "";
    }
    if (!payload.primary_guest_name?.trim()) {
      setError("Ime gosta je obavezno"); toast.error("Ime gosta je obavezno");
      return null;
    }
    if (!payload.unit_id) {
      setError("Odaberite jedinicu"); toast.error("Odaberite jedinicu");
      return null;
    }
    setSaving(true);
    try {
      let saved;
      if (isNew) {
        const { data } = await api.post("/reservations", payload);
        saved = data;
      } else {
        const { data } = await api.put(`/reservations/${reservation.id}`, payload);
        saved = data;
        const rep = data?.ota_report;
        if (rep && rep.status !== "skipped") (rep.status === "ok" ? toast.success : toast.error)(`No-show: ${rep.message}`);
      }
      // Persist guest postal/tax details if this reservation now has a linked guest
      // and any of them actually changed since load (so clearing the last value works too).
      const gid = saved?.primary_guest_id;
      const addrChanged = JSON.stringify(guestAddress) !== JSON.stringify(guestAddressLoadedRef.current);
      if (gid && billingMode === "person" && addrChanged) {
        try {
          await api.put(`/guests/${gid}`, {
            address: guestAddress.address || null,
            city: guestAddress.city || null,
            postal_code: guestAddress.postal_code || null,
            country: guestAddress.country || null,
            oib: guestAddress.oib || null,
            email: guestAddress.email || null,
            phone: guestAddress.phone || null,
          });
        } catch { /* non-fatal */ }
      }
      if (isNew) {
        onSaved(saved);
        onClose();
      } else {
        onSaved(saved);
        setForm((f) => ({ ...f, ...saved }));
        if (!payloadOverride) toast.success("Rezervacija spremljena");
      }
      return saved;
    } catch (e) {
      const moved = !isNew && reservation && (payload.unit_id !== reservation.unit_id || payload.check_in !== reservation.check_in || payload.check_out !== reservation.check_out);
      if (e?.response?.status === 409 && moved && onMoveConflict) {
        const u = units.find((x) => x.id === payload.unit_id);
        onMoveConflict(reservation, { unit_id: payload.unit_id, property_id: u?.property_id || payload.property_id, check_in: payload.check_in, check_out: payload.check_out }, payload);
        return null;
      }
      const msg = formatApiError(e);
      setError(msg);
      toast.error(`Nije spremljeno: ${msg}`);
      setTimeout(() => document.querySelector('[data-testid="drawer-error"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Izbriši ovu rezervaciju?")) return;
    setSaving(true);
    try {
      await api.delete(`/reservations/${reservation.id}`);
      onDeleted(reservation.id);
      onClose();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Quick toggle "paid" — instant save when editing existing reservation
  const togglePaid = async () => {
    const newStatus = form.payment_status === "paid" ? "unpaid" : "paid";
    setForm((f) => ({ ...f, payment_status: newStatus }));
    if (!isNew) {
      try {
        const { data } = await api.put(`/reservations/${reservation.id}`, { payment_status: newStatus });
        onSaved(data);
      } catch (e) {
        setForm((f) => ({ ...f, payment_status: form.payment_status })); // revert
        setError(formatApiError(e));
      }
    }
  };

  // CHECKED IN toggle — instant status change
  const toggleCheckedIn = async () => {
    if (isNew) return;
    const newStatus = form.status === "checked_in" ? "confirmed" : "checked_in";
    try {
      const { data } = await api.put(`/reservations/${reservation.id}`, { status: newStatus });
      setForm((f) => ({ ...f, status: newStatus }));
      onSaved(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  // Task management — instant save
  const addTask = async () => {
    if (!newTaskTitle.trim()) return;
    const task = {
      id: (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()),
      title: newTaskTitle.trim(),
      due_date: newTaskDue || null,
      reminder: !!newTaskDue,
      done: false,
      created_at: new Date().toISOString(),
    };
    const nextTasks = [...(form.tasks || []), task];
    setForm((f) => ({ ...f, tasks: nextTasks }));
    setNewTaskTitle("");
    setNewTaskDue("");
    if (!isNew) {
      try {
        const { data } = await api.put(`/reservations/${reservation.id}`, { tasks: nextTasks });
        onSaved(data);
      } catch (e) { setError(formatApiError(e)); }
    }
  };
  const toggleTask = async (id) => {
    const nextTasks = (form.tasks || []).map((t) => t.id === id ? { ...t, done: !t.done } : t);
    setForm((f) => ({ ...f, tasks: nextTasks }));
    if (!isNew) {
      try { const { data } = await api.put(`/reservations/${reservation.id}`, { tasks: nextTasks }); onSaved(data); } catch (e) { setError(formatApiError(e)); }
    }
  };
  const removeTask = async (id) => {
    const nextTasks = (form.tasks || []).filter((t) => t.id !== id);
    setForm((f) => ({ ...f, tasks: nextTasks }));
    if (!isNew) {
      try { const { data } = await api.put(`/reservations/${reservation.id}`, { tasks: nextTasks }); onSaved(data); } catch (e) { setError(formatApiError(e)); }
    }
  };
  const saveNote = async (nextNoteValue) => {
    // Explicit value avoids stale-state bugs when called right after update("notes", ...).
    const value = nextNoteValue !== undefined ? nextNoteValue : (form.notes || "");
    setForm((f) => ({ ...f, notes: value }));
    setSavedNote(value);
    setEditingNote(!value.trim());
    if (!isNew) {
      try { const { data } = await api.put(`/reservations/${reservation.id}`, { notes: value }); onSaved(data); } catch (e) { setError(formatApiError(e)); }
    }
  };
  const cancelNoteEdit = () => {
    setForm((f) => ({ ...f, notes: savedNote }));
    setEditingNote(!(savedNote || "").trim());
  };

  const unitOptions = useMemo(() => {
    const live = units.filter((u) => u.active !== false || u.id === form.unit_id);
    if (!form.property_id) return live;
    return live.filter((u) => u.property_id === form.property_id);
  }, [form.property_id, form.unit_id, units]);

  const nights = useMemo(() => {
    if (!form.check_in || !form.check_out) return 0;
    return daysBetween(parse(form.check_in), parse(form.check_out));
  }, [form.check_in, form.check_out]);

  const avgPricePerNight = nights > 0 ? (form.total_amount || 0) / nights : 0;
  const isPaid = form.payment_status === "paid";
  const isCheckedIn = form.status === "checked_in";
  const openTasksCount = (form.tasks || []).filter((t) => !t.done).length;

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="reservation-drawer-overlay" />
      <div
        className="rw-sheet-mobile fixed inset-x-0 bottom-0 z-50 flex h-[100dvh] w-full overflow-hidden border-border bg-card shadow-2xl sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:h-[92vh] sm:w-[min(1400px,96vw)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border"
        data-testid="reservation-drawer"
      >
        {/* Left sidebar — narrower on mobile */}
        <aside className="hidden w-16 shrink-0 flex-col items-center justify-between border-r border-border bg-muted/40 py-4 sm:flex">
          <div className="flex flex-col items-center gap-2">
            <NavIconButton id="main" Icon={List} label="Rezervacija" active={activeTab === "main"} onClick={() => setActiveTab("main")} />
            {!isNew && (
              <>
                <NavIconButton id="invoices" Icon={Receipt} label="Računi" active={activeTab === "invoices"} onClick={() => setActiveTab("invoices")} />
                <NavIconButton id="messages" Icon={Envelope} label="Poruke" active={activeTab === "messages"} onClick={() => setActiveTab("messages")} />
              </>
            )}
          </div>
          {!isNew && (
            <button
              type="button"
              onClick={remove}
              disabled={saving}
              className="flex h-10 w-10 items-center justify-center rounded-md text-rose-500 hover:bg-rose-500/10 sm:h-11 sm:w-11"
              title="Izbriši rezervaciju"
              data-testid="drawer-delete"
            >
              <Trash size={20} />
            </button>
          )}
        </aside>

        {/* Right content */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-3 sm:px-6">
            <div className="flex min-w-0 items-center gap-2">
              {!isNew && (
                <>
                  <span className="rounded-md bg-muted px-2 py-1 text-xs font-semibold text-foreground sm:px-2.5" data-testid="res-code">{reservation.code}</span>
                  <span className="hidden rounded-md border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground sm:inline">{SOURCE_LABELS[reservation.source] || reservation.source}</span>
                </>
              )}
              {isNew && <h3 className="truncate text-sm font-semibold text-foreground sm:text-base">Nova rezervacija</h3>}
            </div>
            {!isNew && (
              <div className="flex shrink-0 items-center rounded-md border border-border bg-muted/40 p-0.5 sm:hidden" data-testid="res-tabs-mobile">
                {[{ id: "main", Icon: List, label: "Rezervacija" }, { id: "invoices", Icon: Receipt, label: "Računi" }, { id: "messages", Icon: Envelope, label: "Poruke" }].map((t) => (
                  <button key={t.id} type="button" onClick={() => setActiveTab(t.id)} title={t.label} aria-label={t.label}
                    className={`flex h-7 w-8 items-center justify-center rounded ${activeTab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    data-testid={`res-tab-mobile-${t.id}`}>
                    <t.Icon size={15} weight={activeTab === t.id ? "fill" : "regular"} />
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 sm:gap-3">
              {!isNew && (
                <label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide sm:gap-2 sm:text-xs" data-testid="res-paid-toggle">
                  <span className={isPaid ? "text-emerald-600" : "text-muted-foreground"}>Plaćen</span>
                  <button
                    type="button"
                    onClick={togglePaid}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${isPaid ? "bg-emerald-500" : "bg-muted-foreground/30"}`}
                    data-testid="res-paid-switch"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${isPaid ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                </label>
              )}
              <button onClick={onClose} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="drawer-close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {error && (
              <div className="m-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="drawer-error">{error}</div>
            )}

            {activeTab === "main" && (
              <div className="space-y-5 p-3 sm:p-6">
                {/* Property + Unit + Dates + Guests */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Smještaj">
                    <select className="rw-input" value={form.property_id} onChange={(e) => update("property_id", e.target.value)} data-testid="res-property-select">
                      <option value="">—</option>
                      {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Jedinica">
                    <select className="rw-input" value={form.unit_id} onChange={(e) => update("unit_id", e.target.value)} data-testid="res-unit-select">
                      <option value="">—</option>
                      {unitOptions.map((u) => <option key={u.id} value={u.id}>{u.code}</option>)}
                    </select>
                  </Field>
                </div>

                {/* Dates + Total (hero row) */}
                <div className="rounded-lg border border-border bg-muted/30 p-3 sm:p-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Datumi boravka</p>
                      <div className="grid grid-cols-2 gap-2">
                        <DatePicker value={form.check_in} onChange={(v) => update("check_in", v)} placeholder="Prijava" clearable={false} data-testid="res-checkin" />
                        <DatePicker value={form.check_out} onChange={(v) => update("check_out", v)} placeholder="Odjava" clearable={false} data-testid="res-checkout" />
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {nights} noćenja{nights > 0 && avgPricePerNight > 0 && ` • Prosjek: €${avgPricePerNight.toFixed(2)}/n`}
                      </p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ukupno</p>
                      <div className="flex items-center justify-start gap-2 sm:justify-end">
                        <span className="text-xl font-bold text-foreground">€</span>
                        <input type="number" step="0.01" className="rw-input !w-32 text-right !text-xl !font-bold" value={form.total_amount} onChange={(e) => { setPriceTouched(true); update("total_amount", parseFloat(e.target.value || "0")); }} data-testid="res-total" />
                      </div>
                      {maxPriceExceeded && (
                        <p className="mt-1 text-xs font-semibold text-rose-600" data-testid="res-max-price-warning">
                          Prelazi max cijenu €{Number(maxPrice).toFixed(2)}/noć (najviše €{(Number(maxPrice) * nights).toFixed(2)})
                        </p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {form.adults} odraslih{form.children ? ` · ${form.children} djece` : ""} · <span className="font-medium text-foreground">{SOURCE_LABELS[form.source] || form.source}</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Guests / Adults / Children */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Field label="Odrasli"><input type="number" min="1" className="rw-input" value={form.adults} onChange={(e) => update("adults", parseInt(e.target.value || "1"))} data-testid="res-adults" /></Field>
                  <Field label="Djeca"><input type="number" min="0" className="rw-input" value={form.children} onChange={(e) => update("children", parseInt(e.target.value || "0"))} data-testid="res-children" /></Field>
                  <Field label="Status">
                    <select className="rw-input" value={form.status} onChange={(e) => update("status", e.target.value)} data-testid="res-status">
                      {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    {!isNew && reservation?.source === "booking_com" && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm("Prijaviti Booking.comu nevažeću karticu gosta? Gost dobiva rok da je ažurira.")) return;
                          try {
                            const { data } = await api.post(`/reservations/${reservation.id}/report-invalid-card`);
                            (data.status === "ok" ? toast.success : toast.error)(`Nevažeća kartica: ${data.message}`);
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        className="mt-1 text-[11px] font-semibold text-destructive hover:underline"
                        data-testid="res-report-invalid-card"
                      >
                        Prijavi nevažeću karticu (Booking.com)
                      </button>
                    )}
                    {form.status === "no_show" && reservation?.source === "booking_com" && (
                      <p className="mt-1 text-[10.5px] text-muted-foreground">Spremanjem se no-show prijavljuje Booking.comu (od ponoći na dan dolaska do 48 h nakon odlaska).</p>
                    )}
                  </Field>
                  <Field label="Izvor">
                    <select className="rw-input" value={form.source} onChange={(e) => update("source", e.target.value)} data-testid="res-source">
                      {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </Field>
                </div>

                <BillingPartySection
                  mode={billingMode} setMode={(m) => { setBillingMode(m); if (m === "company" && !form.invoice_guest_name) update("invoice_guest_name", form.primary_guest_name || ""); }} company={company} setCompany={setCompany}
                  form={form} update={update} guestAddress={guestAddress} setGuestAddress={setGuestAddress}
                  reservation={reservation} onOpenProfile={setGuestProfileId}
                />

                {/* Internal note */}
                <div className="rounded-lg border border-border p-3" data-testid="res-note-block">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <Note size={14} weight="bold" /> Interna napomena
                    </div>
                    {!editingNote && (form.notes || "").trim() && (
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setEditingNote(true)} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="res-note-edit"><PencilSimple size={14} /></button>
                        <button type="button" onClick={() => saveNote("")} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-rose-500" data-testid="res-note-delete"><Trash size={14} /></button>
                      </div>
                    )}
                  </div>
                  {editingNote ? (
                    <>
                      <textarea rows="6" className="rw-input" placeholder="Dodaj internu napomenu..." value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} data-testid="res-notes" />
                      <div className="mt-2 flex justify-end gap-2">
                        {(form.notes || "").trim() && (
                          <button type="button" onClick={cancelNoteEdit} className="rounded-md border border-border bg-background px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted" data-testid="res-note-cancel">Odustani</button>
                        )}
                        <button type="button" onClick={() => saveNote()} className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90" data-testid="res-note-save">Spremi napomenu</button>
                      </div>
                    </>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm text-foreground" data-testid="res-note-display">{form.notes}</p>
                  )}
                </div>

                {/* Tasks */}
                <div className="rounded-lg border border-border p-3" data-testid="res-tasks-block">
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <Bell size={14} weight="bold" /> Zadaci {openTasksCount > 0 && <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">{openTasksCount}</span>}
                  </div>
                  <ul className="mb-2 space-y-1.5">
                    {(form.tasks || []).map((t) => (
                      <li key={t.id} className="flex items-center gap-2 text-sm" data-testid={`res-task-${t.id}`}>
                        <input type="checkbox" checked={!!t.done} onChange={() => toggleTask(t.id)} className="h-4 w-4 rounded border-border" data-testid={`res-task-toggle-${t.id}`} />
                        <span className={`flex-1 ${t.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{t.title}</span>
                        {t.due_date && (
                          <span className="flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                            {t.reminder && <Bell size={10} />} {t.due_date}
                          </span>
                        )}
                        <button type="button" onClick={() => removeTask(t.id)} className="rounded p-0.5 text-muted-foreground hover:text-rose-500" data-testid={`res-task-remove-${t.id}`}><Trash size={12} /></button>
                      </li>
                    ))}
                    {(form.tasks || []).length === 0 && <li className="text-xs italic text-muted-foreground">Nema zadataka.</li>}
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    <input className="rw-input flex-1 min-w-[140px]" placeholder="Novi zadatak..." value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} data-testid="res-task-input" />
                    <div className="w-36 shrink-0"><DatePicker value={newTaskDue} onChange={setNewTaskDue} placeholder="Rok" data-testid="res-task-due-input" /></div>
                    <button type="button" onClick={addTask} disabled={!newTaskTitle.trim()} className="rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40" data-testid="res-task-add">
                      <Plus size={14} weight="bold" />
                    </button>
                  </div>
                </div>

                {!isNew && reservation?.id && (
                  <>
                    <GuestStatsSection guestId={form.primary_guest_id || reservation.primary_guest_id} currentReservationId={reservation.id} />
                    <CheckinSection reservationId={reservation.id} initialStatus={reservation.checkin_status} onUpdated={(patch) => { setForm((f) => ({ ...f, ...patch })); onSaved({ ...reservation, ...patch }); }} />
                  </>
                )}
              </div>
            )}

            {activeTab === "invoices" && (
              <InvoicesTab
                reservationId={reservation?.id}
                paymentStatus={form.payment_status}
                onReservationSync={(patch) => { setForm((f) => ({ ...f, ...patch })); onSaved({ ...reservation, ...patch }); }}
              />
            )}

            {activeTab === "messages" && (
              <div className="space-y-4 p-4 sm:p-6" data-testid="res-tab-messages-content">
                {reservation?.id ? (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold text-foreground">Automatske poruke</h4>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setChatFor(reservation)} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted" data-testid="res-messages-open-inbox"><ChatCircleText size={14} /> Inbox gosta</button>
                        <a href="/settings/templates" className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted" data-testid="res-messages-templates-link">Predlošci</a>
                      </div>
                    </div>
                    <TemplateDeliveryLog reservationId={reservation.id} />
                  </>
                ) : <p className="text-sm text-muted-foreground">Spremi rezervaciju da vidiš poruke.</p>}
                {chatFor && <OpenChatDialog reservation={chatFor} onClose={() => setChatFor(null)} />}
              </div>
            )}
          </div>

          {guestProfileId && <GuestPopup guestId={guestProfileId} onClose={() => setGuestProfileId(null)} />}
          {/* Footer actions */}
          {activeTab === "main" && (
            <div className="flex items-center gap-2 border-t border-border bg-card px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:gap-3 sm:bg-muted/20 sm:px-6 sm:pb-3">
              {!isNew && (
                <button type="button" onClick={remove} disabled={saving} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border text-rose-500 hover:bg-rose-500/10 sm:hidden" title="Izbriši rezervaciju" data-testid="drawer-delete-mobile">
                  <Trash size={18} />
                </button>
              )}
              <button
                onClick={() => submit()}
                disabled={saving}
                className="h-10 min-w-0 flex-1 truncate whitespace-nowrap rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                data-testid="drawer-save"
              >
                {saving ? <CircleNotch size={16} className="inline animate-spin" /> : (isNew ? "Kreiraj" : "Spremi")}
              </button>
              {!isNew && (
                <button
                  onClick={toggleCheckedIn}
                  disabled={saving}
                  className={`h-10 shrink-0 whitespace-nowrap rounded-md border px-4 text-sm font-semibold transition ${isCheckedIn ? "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600" : "border-border bg-background text-foreground hover:bg-muted"}`}
                  data-testid="drawer-checked-in"
                >
                  {isCheckedIn ? "Prijavljen" : "Check-in"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`.rw-input { width:100%; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.5rem 0.625rem; font-size:0.875rem; border-radius:0.375rem; color:hsl(var(--foreground)); }
      .rw-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary) / 0.2); }`}</style>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex min-w-0 flex-col justify-end">
      <span className="mb-1 block text-xs font-medium text-foreground">{label}</span>
      {children}
    </label>
  );
}

// ---- Timeline ---------------------------------------------------------------
const DAY_WIDTH = 64; // px — wider cells so reservation blocks and day numbers breathe
const IS_PHONE = typeof window !== "undefined" && window.innerWidth < 640;
const ROW_HEIGHT = IS_PHONE ? 40 : 50;
const INFO_ROW_HEIGHT = IS_PHONE ? 26 : 34;
const BAR_TOP = IS_PHONE ? 5 : 6;
const BAR_HEIGHT = IS_PHONE ? ROW_HEIGHT - 10 : 36;
const BLOCK_HORIZONTAL_GAP = 4; // px gap left+right so adjacent reservations don't touch

const CHANNEL_LABELS = {
  direct: "Standard rate",
  booking_com: "Booking",
  airbnb: "Airbnb",
  expedia: "Expedia",
  pitchup: "Pitchup",
  walk_in: "Walk-in",
};

// Restriction rows shown under "Standard rate" when expanded (Rentlio-style).
// Labels use short abbreviations here (so they fit the narrow left column) but the popover
// editor shows the full name + description.
const RESTRICTION_ROWS = [
  { key: "min_stay", short: "Min stay",          label: "Min. boravak",       fullLabel: "Minimalni boravak",                    desc: "Na odabrane datume gost mora rezervirati najmanje X noći", fmt: (v) => (v == null || v <= 1 ? "" : String(v)), boolean: false },
  { key: "max_stay", short: "Max stay",          label: "Max. boravak",       fullLabel: "Maksimalni boravak",                   desc: "Na odabrane datume gost smije rezervirati najviše X noći", fmt: (v) => (v == null ? "" : String(v)),           boolean: false },
  { key: "min_advance_days", short: "Min unapr.",  label: "Min. za rezervaciju", fullLabel: "Minimalno unaprijed za rezervaciju",  desc: "Rezervacija mora biti napravljena najmanje X dana unaprijed", fmt: (v) => (v == null || v <= 0 ? "" : String(v)), boolean: false },
  { key: "max_advance_days", short: "Max unapr.",  label: "Max. za rezervaciju", fullLabel: "Maksimalno unaprijed za rezervaciju", desc: "Rezervacija se ne može napraviti više od X dana unaprijed", fmt: (v) => (v == null ? "" : String(v)),           boolean: false },
  { key: "no_arrival", short: "Nema dolazaka",        label: "Nema dolazaka",      fullLabel: "Nema dolazaka",                       desc: "Gosti mogu boraviti na te datume, ali ne mogu napraviti check-in.", boolean: true },
  { key: "no_departure", short: "Nema odlaska",      label: "Nema odlazaka",      fullLabel: "Nema odlazaka",                       desc: "Gosti mogu boraviti na te datume, ali ne mogu tada napraviti check-out iz objekta.", boolean: true },
  { key: "stop_sell", short: "Stop Sell",         label: "Zaustavi prodaju",   fullLabel: "Zaustavi prodaju",                    desc: "Datumi nisu dostupni za rezerviranje ni preko jednog kanala.", boolean: true },
];

function computeChannelPrice(base, cfg) {
  if (!cfg || cfg.mode === "same") return base;
  if (cfg.mode === "fixed") return Number(cfg.value || 0);
  if (cfg.mode === "markup") return base * (1 + Number(cfg.value || 0) / 100);
  if (cfg.mode === "markdown") return base * (1 - Number(cfg.value || 0) / 100);
  return base;
}

export default function CalendarPage() {
  const { tenant } = useAuth();
  const { activePropertyId } = useProperty();
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - CAL_LOOKBACK);
    return d;
  });
  const [aheadDays, setAheadDays] = useState(() => {
    const saved = Number(localStorage.getItem("rw-cal-ahead") || 30);
    return CAL_AHEAD_OPTIONS.some((o) => o.n === saved) ? saved : 30;
  });
  const dayCount = CAL_LOOKBACK + aheadDays;
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // { type: 'new'|'edit', reservation?, initialDates? }
  const [dragging, setDragging] = useState(null); // { reservation, mode: 'move'|'resize-start'|'resize-end', anchorX }
  const [hoverCell, setHoverCell] = useState(null); // { unitId, dayIdx } during drag
  const [dragError, setDragError] = useState("");
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const dayHeaderRowRef = useRef(null);
  const [availWidth, setAvailWidth] = useState(0);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const [labelsWidth, setLabelsWidth] = useState(isMobile ? 124 : 192);
  const narrowLabels = isMobile || labelsWidth < 170;

  useEffect(() => {
    let raf = 0;
    const measure = () => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;
      // Use `offsetWidth` instead of `clientWidth` so vertical-scrollbar flipping
      // doesn't change the measurement (classic ResizeObserver infinite-loop
      // bug — screen flickered between two stable DAY_WIDTH values on large
      // monitors where scrollbar sometimes appeared and sometimes didn't).
      const w = scrollEl.offsetWidth - (window.innerWidth < 640 ? 124 : 176) - 20;
      if (w > 0) {
        // Hysteresis — only update when the delta is non-trivial so sub-pixel
        // layout shifts can't bounce us between neighbouring widths.
        setAvailWidth((prev) => (Math.abs(prev - w) > 4 ? w : prev));
      }
      const headerRow = dayHeaderRowRef.current;
      const outer = containerRef.current;
      if (headerRow && outer) {
        const firstCell = headerRow.querySelector("[data-day-cell]");
        if (firstCell) {
          const off = firstCell.getBoundingClientRect().left - outer.getBoundingClientRect().left;
          if (off > 0 && Math.abs(off - labelsWidth) > 1) setLabelsWidth(off);
        }
      }
    };
    const scheduleMeasure = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    scheduleMeasure();
    const ro = new ResizeObserver(scheduleMeasure);
    if (scrollRef.current) ro.observe(scrollRef.current);
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const days = useMemo(() => generateDays(startDate, dayCount), [startDate, dayCount]);
  const endDate = useMemo(() => addDays(startDate, dayCount), [startDate, dayCount]);
  // Adaptive cell width — stretch to fill available viewport but keep a sensible minimum
  const [calZoom, setCalZoom] = useState(() => { try { return localStorage.getItem("rw-cal-zoom") || "compact"; } catch { return "compact"; } });
  const changeZoom = (z) => { setCalZoom(z); try { localStorage.setItem("rw-cal-zoom", z); } catch { /* ignore */ } };
  const MIN_DAY_W = calZoom === "wide" ? (isMobile ? 72 : 96) : (isMobile ? 38 : 48);
  const DAY_WIDTH = availWidth > 0 ? Math.max(MIN_DAY_W, Math.floor(availWidth / CAL_VISIBLE_DAYS)) : Math.max(MIN_DAY_W, 64);
  const TOTAL_WIDTH = dayCount * DAY_WIDTH;
  const [firstVisibleCol, setFirstVisibleCol] = useState(0);
  const onGridScroll = (e) => {
    const col = Math.max(0, Math.floor(e.currentTarget.scrollLeft / DAY_WIDTH));
    setFirstVisibleCol((prev) => (prev === col ? prev : col));
  };
  // Month of the first (left-most) visible day → "LIS. 2026."
  const visibleMonthLabel = useMemo(() => {
    const d = days[Math.min(firstVisibleCol, days.length - 1)];
    if (!d) return "";
    const mon = d.toLocaleDateString("hr-HR", { month: "short" }).replace(".", "").toUpperCase();
    return `${mon}. ${d.getFullYear()}.`;
  }, [days, firstVisibleCol]);

  const [ratePeriods, setRatePeriods] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlansByRt, setRatePlansByRt] = useState({});
  const [collapsedProperties, setCollapsedProperties] = useState(() => new Set());
  const [expandedRestrictions, setExpandedRestrictions] = useState(() => new Set());
  const [pinnedRows, setPinnedRows] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("rw-cal-pinned") || "[]")); }
    catch { return new Set(); }
  });
  const [quickPreview, setQuickPreview] = useState(null); // { reservation, anchorRect }

  // Deep link from Inbox: /calendar?reservation=<id> → jump to the stay and open its drawer.
  useEffect(() => {
    const rid = new URLSearchParams(window.location.search).get("reservation");
    if (!rid) return;
    api.get(`/reservations/${rid}`).then(({ data }) => {
      const d = parse(data.check_in);
      d.setDate(d.getDate() - 3);
      pendingScroll.current = "start";
      sawLoading.current = false;
      setStartDate(d);
      setDrawer({ type: "edit", reservation: data });
      window.history.replaceState(null, "", "/calendar");
    }).catch(() => toast.error("Rezervacija nije pronađena"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [blockPreview, setBlockPreview] = useState(null); // Reservation object (is_block=true) shown in a small confirm-delete popover
  const [editor, setEditor] = useState(null);
  const [rateDrag, setRateDrag] = useState(null);  // { propertyId, propertyName, unitIds, field, channel?, startISO, endISO, startRect, endRect } — drag-to-select for prices/restrictions

  // -----------------------------------------------------------------------
  // Draft Mode — every calendar edit accumulates client-side and is sent
  // to Channex atomically via POST /rate-periods/batch-apply. Guarantees
  // exactly ONE Channex API call per user "Send" action, which is what
  // Channex PMS certification tests #3-#8 require.
  // -----------------------------------------------------------------------
  const DRAFTS_STORAGE_KEY = "pms.calendar.drafts.v1";
  const [drafts, setDrafts] = useState(() => {
    try {
      const raw = localStorage.getItem(DRAFTS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [draftPanelOpen, setDraftPanelOpen] = useState(false);
  const [sendingDrafts, setSendingDrafts] = useState(false);
  useEffect(() => {
    try { localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts)); }
    catch { /* ignore */ }
  }, [drafts]);

  const addDraft = useCallback((draft, silent = false) => {
    setDrafts((prev) => {
      // Replace any existing draft with same scope-key so the latest edit wins.
      const key = (d) => `${d.roomTypeId}|${d.ratePlanId || ""}|${d.field}|${d.channel || ""}|${d.startDate}|${d.endDate}`;
      const k = key(draft);
      const filtered = prev.filter((d) => key(d) !== k);
      return [...filtered, { ...draft, id: draft.id || (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`), createdAt: Date.now() }];
    });
    if (!silent) toast.success("Dodano u draft — pošalji sve odjednom kad završiš");
  }, []);

  const removeDraft = useCallback((id) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const clearDrafts = useCallback(() => setDrafts([]), []);

  // Reservation move drafts — overlaps are allowed ONLY here; publishing validates the final state.
  const MOVE_DRAFTS_KEY = "pms.calendar.moveDrafts.v1";
  const [moveDrafts, setMoveDrafts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(MOVE_DRAFTS_KEY) || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(MOVE_DRAFTS_KEY, JSON.stringify(moveDrafts)); } catch { /* ignore */ }
  }, [moveDrafts]);
  const [moveConfirm, setMoveConfirm] = useState(null);
  const [calGuestId, setCalGuestId] = useState(null);
  const moveConflictsRef = useRef(new Set());
  const addMoveDraft = useCallback((r, target) => {
    setMoveDrafts((prev) => {
      const next = { ...prev };
      const orig = prev[r.id]?.orig || { unit_id: r.unit_id, check_in: r.check_in, check_out: r.check_out };
      if (orig.unit_id === target.unit_id && orig.check_in === target.check_in && orig.check_out === target.check_out) delete next[r.id];
      else next[r.id] = { ...target, orig, code: r.code, guest: r.primary_guest_name };
      return next;
    });
  }, []);
  const removeMoveDraft = useCallback((id) => setMoveDrafts((prev) => { const n = { ...prev }; delete n[id]; return n; }), []);

  const sendDrafts = useCallback(async () => {
    // Detect any pending block drafts on the server so we push them
    // in the SAME "Objavi drafts" action — user should never have to
    // publish rate drafts and block drafts separately.
    const blockDraftCount = reservations.filter((r) => r.is_block && r.is_draft).length;
    const moveList = Object.entries(moveDrafts);
    if (drafts.length === 0 && blockDraftCount === 0 && moveList.length === 0) return;
    if (moveConflictsRef.current.size > 0) {
      toast.error("U draftu su dvije rezervacije u istoj jedinici (označeno crveno). Razriješi preklapanje prije spremanja.");
      setDraftPanelOpen(true);
      return;
    }
    setSendingDrafts(true);
    try {
      let movedCount = 0;
      let unitsEnqueuedMoves = 0;
      if (moveList.length > 0) {
        try {
          const { data } = await api.post("/reservations/batch-move", {
            moves: moveList.map(([id, d]) => ({ reservation_id: id, unit_id: d.unit_id, check_in: d.check_in, check_out: d.check_out })),
          });
          movedCount = data?.reservations?.length || 0;
          unitsEnqueuedMoves = data?.units_enqueued || 0;
          setMoveDrafts({});
        } catch (e) {
          const det = e?.response?.data?.detail;
          if (e?.response?.status === 404) {
            const ids = Object.keys(moveDrafts).filter((id) => !reservations.some((x) => x.id === id) || String(det || "").includes(id));
            if (ids.length) setMoveDrafts((prev) => { const n = { ...prev }; ids.forEach((id) => delete n[id]); return n; });
            toast.error("Neka rezervacija iz drafta više ne postoji — uklonjena je iz drafta, pokušaj ponovno.");
            setSendingDrafts(false);
            return;
          }
          if (det?.conflicts) {
            const c = det.conflicts[0];
            toast.error(`${det.message} Npr. ${c.unit}: ${c.a.guest || c.a.code} i ${c.b.guest || c.b.code}.`);
            setSendingDrafts(false);
            return;
          }
          throw e;
        }
      }
      let appliedRateChanges = 0;
      let unitsEnqueued = unitsEnqueuedMoves;
      if (drafts.length > 0) {
        const payload = {
          changes: drafts.map((d) => ({
            start_date: d.startDate,
            end_date: d.endDate,
            unit_ids: d.unitIds || [],
            field: d.field === "rate_plan_base_price" ? "base_price" : d.field,
            channel: d.channel || null,
            rate_plan_id: d.ratePlanId || null,
            value: d.newValue,
          })),
          reason: "draft_batch_apply",
        };
        const { data } = await api.post("/rate-periods/batch-apply", payload);
        appliedRateChanges = data?.applied_changes || drafts.length;
        unitsEnqueued += data?.units_enqueued || 0;
      }
      let publishedBlocks = 0;
      if (blockDraftCount > 0) {
        const { data } = await api.post("/unit-blocks/publish-drafts");
        publishedBlocks = data?.published || 0;
        unitsEnqueued += data?.units_enqueued || 0;
      }
      const parts = [];
      if (appliedRateChanges) parts.push(`${appliedRateChanges} cijenskih promjena`);
      if (publishedBlocks) parts.push(`${publishedBlocks} blokada`);
      if (movedCount) parts.push(`${movedCount} premještenih rezervacija`);
      toast.success(
        `Poslano: ${parts.join(" + ") || "0"} · ${unitsEnqueued} sync task(ova) u redu`,
      );
      setDrafts([]);
      setDraftPanelOpen(false);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Greška pri slanju";
      toast.error(`Nije uspjelo: ${detail}`);
    } finally {
      setSendingDrafts(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts, reservations, moveDrafts]);

  const togglePin = useCallback((key) => {
    setPinnedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try { localStorage.setItem("rw-cal-pinned", JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const toggleProperty = useCallback((pid) => {
    setCollapsedProperties((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  }, []);

  // Derived rate plans (non-default) are hidden under BAR until expanded (not persisted)
  const [expandedDerived, setExpandedDerived] = useState(() => new Set());
  const toggleDerived = (rtId) => setExpandedDerived((prev) => {
    const next = new Set(prev);
    if (next.has(rtId)) next.delete(rtId); else next.add(rtId);
    return next;
  });
  const visibleRatePlans = (rti) => {
    const def = rti.ratePlans.find((rp) => rp.is_default);
    if (!def) return rti.ratePlans;
    const rest = rti.ratePlans.filter((rp) => rp.id !== def.id);
    return expandedDerived.has(rti.rt.id) ? [def, ...rest] : [def];
  };

  const toggleRestrictions = useCallback((pid) => {
    setExpandedRestrictions((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  }, []);

  // Global mouseUp completes a drag-to-select on price/restriction cells → opens editor with range
  useEffect(() => {
    if (!rateDrag) return;
    const onUp = () => {
      setRateDrag((d) => {
        if (!d) return null;
        // Determine start/end and anchor rect (pick whichever is left-most and top-most to attach popover)
        const swap = d.endISO < d.startISO;
        const startISO = swap ? d.endISO : d.startISO;
        const endISO = swap ? d.startISO : d.endISO;
        const anchorRect = swap ? d.endRect : d.startRect;
        setEditor({
          anchorRect,
          propertyId: d.propertyId,
          propertyName: d.propertyName,
          date: startISO,
          endDate: endISO,
          field: d.field,
          channel: d.channel,
          currentValue: d.currentValue,
          unitIds: d.unitIds,
          roomTypeId: d.roomTypeId,
          roomTypeName: d.roomTypeName,
          ratePlanId: d.ratePlanId,
          ratePlanName: d.ratePlanName,
        });
        return null;
      });
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [rateDrag]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, uRes, rRes, rpRes, rtRes] = await Promise.all([
        api.get("/properties"),
        api.get("/units", { params: { include_inactive: true } }),
        api.get("/reservations", { params: { start: fmt(startDate), end: fmt(endDate), include_blocks: true } }),
        api.get("/rate-periods"),
        api.get("/room-types"),
      ]);
      setProperties(pRes.data);
      setUnits(uRes.data);
      setReservations(rRes.data);
      setRatePeriods(rpRes.data || []);
      setRoomTypes(rtRes.data || []);
      // Fetch rate plans for every room type in parallel
      const plansEntries = await Promise.all(
        (rtRes.data || []).map(async (rt) => {
          try {
            const rr = await api.get(`/room-types/${rt.id}/rate-plans`);
            return [rt.id, rr.data || []];
          } catch { return [rt.id, []]; }
        })
      );
      setRatePlansByRt(Object.fromEntries(plansEntries));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { refresh(); }, [refresh]);

  // ---------------------------------------------------------------------------
  // Auto-refresh (polling our OWN /reservations endpoint) — picks up new OTA
  // bookings that arrived via Channex webhook into our Mongo while the user
  // is viewing the calendar. ZERO Channex API calls — pure Mongo query.
  // Interval: 30 s, pauses when tab is hidden to save battery.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let interval = null;
    const pollNewReservations = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data: fresh } = await api.get("/reservations", {
          params: { start: fmt(startDate), end: fmt(endDate), include_blocks: true },
        });
        setReservations((prev) => {
          const prevIds = new Set(prev.map((r) => r.id));
          const added = fresh.filter((r) => !prevIds.has(r.id) && !r.is_block);
          if (added.length) {
            // Toast each new reservation (max 3 to avoid spam on bulk sync)
            added.slice(0, 3).forEach((r) => {
              const unitName = units.find((u) => u.id === r.unit_id)?.code || "jedinica";
              toast.success(`Nova rezervacija · ${r.primary_guest_name || "gost"}`, {
                description: `${unitName} · ${r.check_in} → ${r.check_out}${r.source ? ` · ${SOURCE_LABELS[r.source] || r.source}` : ""}`,
                duration: 8000,
              });
            });
            if (added.length > 3) toast.success(`+${added.length - 3} dodatnih novih rezervacija`);
          }
          return fresh;
        });
      } catch {
        // ignore transient errors — next tick retries
      }
    };
    interval = setInterval(pollNewReservations, 30000);
    const onVis = () => { if (document.visibilityState === "visible") pollNewReservations(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, units.length]);

  // Group units by property (filtered by active property switcher, if any)
  const grouped = useMemo(() => {
    const propList = activePropertyId ? properties.filter((p) => p.id === activePropertyId) : properties;
    // F8.7: respect Property.display_order so user can reorder which property
    // appears at the top of the calendar. Tie-break by name for stability.
    const sortedProps = [...propList].sort((a, b) => {
      const ao = a.display_order ?? 0;
      const bo = b.display_order ?? 0;
      if (ao !== bo) return ao - bo;
      return (a.name || "").localeCompare(b.name || "");
    });
    // Removed (inactive) units are hidden; one is kept only while it still holds a live booking.
    const bookedUnitIds = new Set(reservations.filter((r) => r.status !== "cancelled").map((r) => r.unit_id));
    const visible = units.filter((u) => u.active !== false || bookedUnitIds.has(u.id));
    return sortedProps
      .filter((p) => visible.some((u) => u.property_id === p.id))
      .map((p) => ({
        property: p,
        units: visible.filter((u) => u.property_id === p.id).sort((a, b) => a.display_order - b.display_order),
      }));
  }, [properties, units, activePropertyId, reservations]);

  // Build a flat ordered list of unit IDs to compute "which unit row is at Y px"
  const unitRowOrder = useMemo(() => {
    const out = [];
    grouped.forEach((g) => {
      out.push({ type: "header", propertyId: g.property.id });
      g.units.forEach((u) => out.push({ type: "unit", unit: u }));
    });
    return out;
  }, [grouped]);

  const todayCol = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const idx = daysBetween(startDate, today);
    return idx >= 0 && idx < dayCount ? idx : -1;
  }, [startDate, dayCount]);

  // Land on today's column; the 30 days of history stay reachable by scrolling left.
  const autoScrollKey = useRef("");
  const pendingScroll = useRef(null); // "start" | "end" after an arrow extended the range
  const sawLoading = useRef(false);
  useEffect(() => {
    if (loading) { autoScrollKey.current = ""; sawLoading.current = true; return; }
    const el = scrollRef.current;
    if (!el) return;
    if (pendingScroll.current) {
      if (!sawLoading.current) return; // wait until the extended range has reloaded
      const p = pendingScroll.current;
      pendingScroll.current = null;
      sawLoading.current = false;
      autoScrollKey.current = `${fmt(startDate)}-${DAY_WIDTH}`;
      requestAnimationFrame(() => { el.scrollLeft = p === "end" ? el.scrollWidth : 0; });
      return;
    }
    if (todayCol < 0) return;
    const key = `${fmt(startDate)}-${DAY_WIDTH}`;
    if (autoScrollKey.current === key) return;
    autoScrollKey.current = key;
    requestAnimationFrame(() => { el.scrollLeft = Math.max(0, todayCol - 2) * DAY_WIDTH; });
  }, [loading, todayCol, DAY_WIDTH, startDate]);

  // Grid fills the viewport down to the bottom nav (no dead gap, no page scroll).
  const [gridMaxH, setGridMaxH] = useState(null);
  useEffect(() => {
    const calc = () => {
      const el = scrollRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const reserve = window.innerWidth < 1024 ? 64 + 10 : 12;
      setGridMaxH(Math.max(260, window.innerHeight - top - reserve));
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [loading]);

  // F6.13 — Local ISO date "today" used to lock all cells for dates in the
  // past. Keeps the frontend aligned with the backend `_assert_not_past_edit`
  // guard so operators can't even attempt to drag-select or click past cells.
  const todayISO = useMemo(() => {
    const t = new Date();
    const yyyy = t.getFullYear();
    const mm = String(t.getMonth() + 1).padStart(2, "0");
    const dd = String(t.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const displayReservations = useMemo(() => reservations.map((r) => {
    const d = moveDrafts[r.id];
    return d ? { ...r, unit_id: d.unit_id, property_id: d.property_id, check_in: d.check_in, check_out: d.check_out, _moveDraft: true } : r;
  }), [reservations, moveDrafts]);
  const reservationsByUnit = useMemo(() => {
    const m = {};
    displayReservations.forEach((r) => { (m[r.unit_id] ||= []).push(r); });
    return m;
  }, [displayReservations]);
  // Reservations that overlap another one in the same unit (possible only through drafts)
  const moveConflicts = useMemo(() => {
    const bad = new Set();
    Object.values(reservationsByUnit).forEach((list) => {
      const act = list.filter((r) => r.status !== "cancelled" && r.status !== "no_show");
      for (let i = 0; i < act.length; i++) for (let j = i + 1; j < act.length; j++) {
        const a = act[i]; const b = act[j];
        if ((a._moveDraft || b._moveDraft) && a.check_in < b.check_out && b.check_in < a.check_out) { bad.add(a.id); bad.add(b.id); }
      }
    });
    return bad;
  }, [reservationsByUnit]);
  moveConflictsRef.current = moveConflicts;
  const findUnitConflict = (resId, unitId, ci, co) => (reservationsByUnit[unitId] || []).find(
    (x) => x.id !== resId && x.status !== "cancelled" && x.status !== "no_show" && x.check_in < co && ci < x.check_out,
  );

  // Discover channel slugs for a unit group from rate periods + always include "direct"
  const channelsForGroup = useCallback((groupUnitIds) => {
    const set = new Set(["direct"]);
    ratePeriods.forEach((p) => {
      if (p.active === false) return;
      const uids = p.unit_ids || [];
      if (uids.length > 0 && !uids.some((id) => groupUnitIds.has(id))) return;
      Object.keys(p.channels || {}).forEach((ch) => set.add(ch));
    });
    return Array.from(set);
  }, [ratePeriods]);

  // Room types present in a property group, with their rate plans + units.
  const roomTypesForGroup = useCallback((groupUnits) => {
    const rtMap = new Map();
    for (const u of groupUnits) {
      const rtId = u.room_type_id;
      if (!rtId) continue;
      if (!rtMap.has(rtId)) {
        const rt = roomTypes.find((r) => r.id === rtId);
        rtMap.set(rtId, { rt, unitIds: [], ratePlans: ratePlansByRt[rtId] || [] });
      }
      rtMap.get(rtId).unitIds.push(u.id);
    }
    return Array.from(rtMap.values()).filter((r) => r.rt);
  }, [roomTypes, ratePlansByRt]);

  // Every room type (all properties) for the multi-room-type cell editor.
  const editorRoomTypeOptions = useMemo(() => properties.flatMap((p) =>
    roomTypesForGroup(units.filter((u) => u.property_id === p.id && u.active !== false))
      .map((r) => ({ ...r, propertyId: p.id, propertyName: p.name }))
  ), [properties, units, roomTypesForGroup]);


  const applyRatePlanModifier = (base, mode, value) => {
    const b = Number(base || 0);
    const v = Number(value || 0);
    if (!mode || mode === "same") return b;
    if (mode === "standalone") return Math.max(v, 0);
    if (mode === "fixed") return Math.round((b + v) * 100) / 100;
    if (mode === "markup") return Math.round(b * (1 + v / 100) * 100) / 100;
    if (mode === "markdown") return Math.round(b * (1 - v / 100) * 100) / 100;
    return b;
  };

  // Best base price for (roomTypeId, rate_plan_id, iso). Follows priority rules:
  // 1. Period explicitly scoped to this rate_plan_id (own_period)
  // 2. Generic period (rate_plan_id: null) targeting these units
  // 3. Fallback unit base_price
  // NOTE: Periods flagged `restriction_only` are SKIPPED (they carry only min_stay/stop_sell/etc.
  // and their `base_price` field is a meaningless 0). This prevents a stray "quick restriction edit"
  // from wiping the price.
  const priceForRoomTypeRatePlan = useCallback((rt, rp, unitIds, iso) => {
    const unitIdSet = new Set(unitIds);
    // Collect ALL matching candidates then pick winner by priority DESC → created_at DESC.
    // This matches backend `_find_period_for_date_and_rate_plan` semantics so single-date
    // overrides (newest, same priority) win over broader overlapping periods on display.
    // We also collect BAR-scoped periods separately so a change to BAR immediately
    // propagates to derived plan displays via the cascade (BAR + modifier).
    const rtRatePlans = ratePlansByRt[rt.id] || [];
    const defaultRp = rtRatePlans.find((r) => r.is_default);
    const ownCandidates = [];
    const genericCandidates = [];
    const barCandidates = [];
    for (const p of ratePeriods) {
      if (p.active === false) continue;
      if (p.restriction_only) continue;
      if (!(p.start_date <= iso && iso <= p.end_date)) continue;
      const uids = p.unit_ids || [];
      if (uids.length > 0 && !uids.some((id) => unitIdSet.has(id))) continue;
      if (p.rate_plan_id === rp.id) ownCandidates.push(p);
      else if (!p.rate_plan_id) genericCandidates.push(p);
      else if (defaultRp && p.rate_plan_id === defaultRp.id) barCandidates.push(p);
    }
    const pickBest = (arr) => {
      if (!arr.length) return null;
      arr.sort((a, b) => {
        const priDiff = (b.priority || 0) - (a.priority || 0);
        if (priDiff !== 0) return priDiff;
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
      return arr[0];
    };
    const ownPeriod = pickBest(ownCandidates);
    const genericPeriod = pickBest(genericCandidates);
    const barPeriod = pickBest(barCandidates);
    // "round_up" (this rate's "zaokruži na puni broj" setting) always ceils the
    // final nightly price shown/edited here — it must match what gets pushed
    // to OTAs (backend applies the same ceil at push time in sync.py).
    const roundIfNeeded = (v) => (v != null && rp.round_up ? Math.ceil(v - 1e-9) : v);
    // Anchor for derived: default (BAR) period value; for BAR itself → its own or generic period
    if (rp.is_default) {
      const src = ownPeriod || genericPeriod;
      // F6.11: no more rt.base_price fallback. Returns null → cell is "closed".
      return roundIfNeeded(src ? Number(src.base_price || 0) : null);
    }
    // Derived rate plan cascade (mirrors backend `_compute_rate_values`):
    //   1) own_period → user typed a FINAL price for THIS plan (no modifier)
    //   2) BAR-scoped period → anchor for cascade (apply modifier)
    //   3) generic period → anchor for cascade (apply modifier)
    // F6.11: Removed `rt.base_price` fallback. If no anchor exists, cell is "closed".
    if (ownPeriod) return roundIfNeeded(Number(ownPeriod.base_price || 0));
    const anchorPeriod = barPeriod || genericPeriod;
    if (!anchorPeriod) return null;
    const anchor = Number(anchorPeriod.base_price || 0);
    return roundIfNeeded(applyRatePlanModifier(anchor, rp.price_modifier_mode, rp.price_modifier_value));
  }, [ratePeriods, ratePlansByRt]);

  // Active rate period for a group on a given ISO date.
  // Tie-break: higher priority wins, and on priority tie the NEWER (later created_at) wins,
  // so a single-date override created after a group edit takes precedence for that date.
  // If `forPrice=true`, periods flagged `restriction_only` are filtered out (they carry no price).
  const periodForGroupOnDate = useCallback((groupUnitIds, iso, forPrice = false) => {
    const matches = ratePeriods.filter((p) => {
      if (p.active === false) return false;
      if (forPrice && p.restriction_only) return false;
      if (p.start_date > iso || p.end_date < iso) return false;
      const uids = p.unit_ids || [];
      if (uids.length === 0) return true;
      return uids.some((id) => groupUnitIds.has(id));
    });
    if (!matches.length) return null;
    matches.sort((a, b) => {
      const priDiff = (b.priority || 0) - (a.priority || 0);
      if (priDiff !== 0) return priDiff;
      // Newer wins on priority tie — matches backend `_find_period_for_date`
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return matches[0];
  }, [ratePeriods]);

  const priceForChannelOnDate = useCallback((groupUnits, groupUnitIds, channel, iso) => {
    const period = periodForGroupOnDate(groupUnitIds, iso, /* forPrice= */ true);
    if (period) {
      const base = Number(period.base_price || 0);
      const cfg = (period.channels || {})[channel];
      return computeChannelPrice(base, cfg);
    }
    // F6.11: no RatePeriod → no price (date not bookable).
    return null;
  }, [periodForGroupOnDate]);

  // Effective restriction value for the direct/period-level view.
  // (Per-channel restriction overrides can be surfaced later.)
  const restrictionForGroupOnDate = useCallback((groupUnitIds, iso, field) => {
    const period = periodForGroupOnDate(groupUnitIds, iso);
    if (!period) return null;
    return period[field];
  }, [periodForGroupOnDate]);

  // Find the best-matching period for (units, date) scoped to a specific rate plan.
  // Selection precedence matches backend `_find_period_for_date_and_rate_plan`:
  //   1) Period scoped to THIS rate plan wins over generic (`rate_plan_id: null`).
  //   2) Higher priority wins on tie.
  //   3) Newer created_at wins on tie.
  // Periods scoped to a DIFFERENT rate plan are ignored entirely.
  const findPeriodForRatePlan = useCallback((groupUnitIds, iso, ratePlanId) => {
    const candidates = ratePeriods.filter((p) => {
      if (p.active === false) return false;
      if (p.start_date > iso || p.end_date < iso) return false;
      const uids = p.unit_ids || [];
      return uids.length === 0 || uids.some((id) => groupUnitIds.has(id));
    }).sort((a, b) => {
      const priDiff = (b.priority || 0) - (a.priority || 0);
      if (priDiff !== 0) return priDiff;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    let scoped = null, generic = null;
    for (const p of candidates) {
      const p_rp = p.rate_plan_id || null;
      if (p_rp === ratePlanId) { if (!scoped) scoped = p; }
      else if (p_rp === null) { if (!generic) generic = p; }
      // scoped to a different rate plan → skip
    }
    return scoped || generic;
  }, [ratePeriods]);

  // Effective restriction value scoped to a specific rate plan.
  const restrictionForRatePlanOnDate = useCallback((groupUnitIds, iso, field, ratePlanId) => {
    const period = findPeriodForRatePlan(groupUnitIds, iso, ratePlanId);
    return period ? period[field] : null;
  }, [findPeriodForRatePlan]);

  // Draft overlay lookup — key: `${roomTypeId}|${ratePlanId||""}|${field}|${channel||""}|${iso}`.
  // Each entry is the pending `newValue` waiting to be pushed via "Spremi promjene".
  // Used to render draft cells with an amber dashed border so the operator sees
  // WHERE their pending changes land without opening the draft panel.
  const draftLookup = useMemo(() => {
    const m = new Map();
    for (const d of drafts) {
      const s = d.startDate, e = d.endDate;
      if (!s || !e) continue;
      const start = new Date(s + "T00:00:00");
      const end = new Date(e + "T00:00:00");
      for (let t = start; t <= end; t = addDays(t, 1)) {
        const iso = fmt(t);
        const key = `${d.roomTypeId || ""}|${d.ratePlanId || ""}|${d.field}|${d.channel || ""}|${iso}`;
        m.set(key, d.newValue);
      }
    }
    return m;
  }, [drafts]);

  const draftValueFor = useCallback((roomTypeId, ratePlanId, field, iso, channel) => {
    const key = `${roomTypeId || ""}|${ratePlanId || ""}|${field}|${channel || ""}|${iso}`;
    return draftLookup.has(key) ? draftLookup.get(key) : undefined;
  }, [draftLookup]);

  const vacantUnitsForDate = useCallback((groupUnits, iso) => {
    const reservedIds = new Set();
    displayReservations.forEach((r) => {
      if (r.status === "cancelled" || r.status === "no_show") return;
      if (r.check_in <= iso && iso < r.check_out) reservedIds.add(r.unit_id);
    });
    return groupUnits.filter((u) => !reservedIds.has(u.id)).length;
  }, [displayReservations]);

  // Calculate reservation block position — semantics:
  // • check_in = afternoon of that day (block starts at MIDDLE of check-in cell)
  // • check_out = morning of that day (block ends at MIDDLE of check-out cell)
  // • This means a 5-night stay from 20 → 25 visually spans from mid-20 to mid-25
  //   (covering nights of 20, 21, 22, 23, 24 — 5 nights).
  // • Next guest can check in afternoon of 25, their block starts at mid-25 (touching previous).
  const blockFor = (r) => {
    const ci = parse(r.check_in);
    const co = parse(r.check_out);
    const ciIdx = daysBetween(startDate, ci);  // can be negative if check_in is before visible range
    const coIdx = daysBetween(startDate, co);  // can exceed dayCount

    // Mid-day pixel position
    const ciMid = ciIdx * DAY_WIDTH + DAY_WIDTH / 2;
    const coMid = coIdx * DAY_WIDTH + DAY_WIDTH / 2;

    // Clamp to visible viewport
    const left = Math.max(0, ciMid);
    const right = Math.min(dayCount * DAY_WIDTH, coMid);

    if (right <= 0 || left >= dayCount * DAY_WIDTH) return null;
    return { left, width: Math.max(right - left, 12) };
  };

  const onDayCellClick = (unit, day) => {
    if (dragging) return;
    // In block mode the window `mouseup` finish() handler already created the
    // block (mousedown → mouseup fires before onClick). We must NOT create
    // another block here — that caused a "double draft" bug.
    if (inputModeRef.current === "block") return;
    setDrawer({
      type: "new",
      initialDates: {
        unit_id: unit.id,
        property_id: unit.property_id,
        check_in: fmt(day),
        check_out: fmt(addDays(day, 1)),
      },
    });
  };

  // Drag-to-create: user presses on a unit cell, drags across N cells, releases → opens NEW reservation drawer with pre-filled date range
  const [newResDrag, setNewResDrag] = useState(null); // { unitId, propertyId, startISO, endISO }
  // Keep a ref in sync so the global mouseup listener always sees the LATEST
  // drag state, regardless of React's render batching. Without this, quick
  // successive drags (create block → immediately drag another) would run
  // finish() with a stale closure and effectively swallow the 2nd drag.
  const newResDragRef = useRef(null);
  useEffect(() => { newResDragRef.current = newResDrag; }, [newResDrag]);

  // Input mode: default is "reservation" (drag creates a booking); switch to "block"
  // to drag out grey-hatched maintenance/owner blocks that decrement availability
  // WITHOUT looking like a guest booking. Blocks default to draft-mode so the
  // user batches them alongside rate/restriction edits before pushing to Channex.
  // Required for Channex Cert Test #9 / #10 (availability updates without touching
  // the reservation flow).
  const [inputMode, setInputMode] = useState(() => {
    try { return localStorage.getItem("rw-cal-input-mode") === "block" ? "block" : "reservation"; }
    catch { return "reservation"; }
  });
  const inputModeRef = useRef(inputMode);
  const [blockDialog, setBlockDialog] = useState(false);
  useEffect(() => {
    inputModeRef.current = inputMode;
    try { localStorage.setItem("rw-cal-input-mode", inputMode); } catch { /* ignore */ }
  }, [inputMode]);

  const startNewResDrag = (unit, day) => {
    const next = { unitId: unit.id, propertyId: unit.property_id, startISO: fmt(day), endISO: fmt(day) };
    newResDragRef.current = next;
    setNewResDrag(next);
  };
  const extendNewResDrag = (day) => {
    if (!newResDragRef.current) return;
    setNewResDrag((s) => (s ? { ...s, endISO: fmt(day) } : s));
  };

  // Single, stable mouseup listener that reads the LATEST drag state via ref.
  // Fixes race condition where quickly-successive drags (create block → drag
  // another) would run stale finish closures and silently drop the 2nd drag.
  useEffect(() => {
    const finish = async () => {
      const drag = newResDragRef.current;
      if (!drag) return;
      const s = drag.startISO;
      const e = drag.endISO;
      const [a, b] = s <= e ? [s, e] : [e, s];
      // Drag from 20. to 25. → stay 20.–25. (25. is check-out). Single-cell click-drag = 1 night.
      const chkOut = a === b ? fmt(addDays(parse(b), 1)) : b;
      const capturedUnitId = drag.unitId;
      const capturedPropertyId = drag.propertyId;
      const mode = inputModeRef.current;
      // Clear state IMMEDIATELY so the next mousedown can start a fresh drag
      // even if the API call below hasn't resolved yet.
      newResDragRef.current = null;
      setNewResDrag(null);
      if (mode === "block") {
        try {
          const { data } = await api.post("/unit-blocks", {
            unit_id: capturedUnitId,
            check_in: a,
            check_out: chkOut,
            block_reason: "Blokada iz kalendara",
            is_draft: true,
          });
          setReservations((rs) => [...rs, data]);
          toast.success(`Blokada dodana kao draft · ${a} → ${chkOut} · pošalji sve odjednom kad završiš`);
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || "Greška pri kreiranju blokade";
          toast.error(detail);
        }
        return;
      }
      setDrawer({
        type: "new",
        initialDates: {
          unit_id: capturedUnitId,
          property_id: capturedPropertyId,
          check_in: a,
          check_out: chkOut,
        },
      });
    };
    window.addEventListener("mouseup", finish);
    return () => window.removeEventListener("mouseup", finish);
  }, []);

  const onReservationSaved = (saved) => {
    setReservations((rs) => {
      const idx = rs.findIndex((r) => r.id === saved.id);
      if (idx >= 0) {
        const copy = [...rs]; copy[idx] = saved; return copy;
      }
      return [...rs, saved];
    });
  };

  // Listen for reservation patches coming from QuickReservationPreview so the
  // calendar list stays in sync (note/tasks/paid toggle etc).
  useEffect(() => {
    const onPatched = (e) => {
      const d = e?.detail;
      if (!d?.id) return;
      setReservations((rs) => {
        const idx = rs.findIndex((r) => r.id === d.id);
        if (idx < 0) return rs;
        const copy = [...rs]; copy[idx] = { ...rs[idx], ...d }; return copy;
      });
    };
    window.addEventListener("rw-reservation-updated", onPatched);
    return () => window.removeEventListener("rw-reservation-updated", onPatched);
  }, []);

  const onReservationDeleted = (id) => {
    setReservations((rs) => rs.filter((r) => r.id !== id));
  };

  // ------ Drag & Drop ----------------------------------------------------
  const onBlockDragStart = (e, r, mode) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData
    try { e.dataTransfer.setData("text/plain", r.id); } catch (_e) { /* noop */ }
    const ghost = document.createElement("div");
    ghost.style.opacity = "0";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
    setDragging({
      reservation: r,
      mode,
      origCheckIn: r.check_in,
      origCheckOut: r.check_out,
      origUnitId: r.unit_id,
    });
    setDragError("");
  };

  const onCellDragOver = (e, unit, dayIdx) => {
    if (!dragging) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setHoverCell({ unitId: unit.id, dayIdx });
  };

  const onCellDrop = async (e, unit, day, dayIdx) => {
    if (!dragging) return;
    e.preventDefault();
    e.stopPropagation();
    const { reservation: r, mode } = dragging;
    const origCi = parse(r.check_in);
    const origCo = parse(r.check_out);
    const nights = daysBetween(origCi, origCo);

    let newCheckIn = r.check_in;
    let newCheckOut = r.check_out;
    let newUnitId = r.unit_id;

    if (mode === "move") {
      // Move whole reservation to start on `day` and keep nights, change unit if dropped on different row
      newCheckIn = fmt(day);
      newCheckOut = fmt(addDays(day, nights));
      newUnitId = unit.id;
    } else if (mode === "resize-start") {
      // Day = new check-in; check-out unchanged; unit must remain
      const newCi = day;
      if (newCi >= origCo) {
        setDragError("Dolazak ne može biti nakon ili na isti dan kao odlazak");
        setDragging(null); setHoverCell(null);
        return;
      }
      newCheckIn = fmt(newCi);
    } else if (mode === "resize-end") {
      // Day = new check-out; check-in unchanged
      const newCo = addDays(day, 1); // dropping on a day means check-out happens day+1 morning (Booking semantics)
      if (newCo <= origCi) {
        setDragError("Odlazak ne može biti prije dolaska");
        setDragging(null); setHoverCell(null);
        return;
      }
      newCheckOut = fmt(newCo);
    }

    setDragging(null);
    setHoverCell(null);

    if (newCheckIn === r.check_in && newCheckOut === r.check_out && newUnitId === r.unit_id) return;

    if (!r.is_block) {
      const target = { unit_id: newUnitId, property_id: unit.property_id, check_in: newCheckIn, check_out: newCheckOut };
      if (r._moveDraft) { addMoveDraft(r, target); return; }
      const clash = findUnitConflict(r.id, newUnitId, newCheckIn, newCheckOut);
      if (clash) { setMoveConfirm({ reservation: r, target, clash }); return; }
      // Free in the draft view but still occupied on the server (someone was drafted away) → continue the draft
      const serverClash = reservations.some((x) => x.id !== r.id && x.unit_id === newUnitId && x.status !== "cancelled" && x.status !== "no_show" && x.check_in < newCheckOut && newCheckIn < x.check_out);
      if (serverClash) { addMoveDraft(r, target); toast.success("Dodano u draft — spremi promjene kad završiš raspored"); return; }
    }

    // Optimistic update
    const optimistic = { ...r, check_in: newCheckIn, check_out: newCheckOut, unit_id: newUnitId, nights: daysBetween(parse(newCheckIn), parse(newCheckOut)) };
    setReservations((rs) => rs.map((x) => x.id === r.id ? optimistic : x));

    try {
      const { data } = await api.put(`/reservations/${r.id}`, {
        check_in: newCheckIn,
        check_out: newCheckOut,
        unit_id: newUnitId,
        property_id: unit.property_id,
      });
      onReservationSaved(data);
    } catch (e) {
      // Revert
      setReservations((rs) => rs.map((x) => x.id === r.id ? r : x));
      if (e?.response?.status === 409 && !r.is_block) {
        setMoveConfirm({ reservation: r, target: { unit_id: newUnitId, property_id: unit.property_id, check_in: newCheckIn, check_out: newCheckOut }, clash: null });
        return;
      }
      setDragError(formatApiError(e));
    }
  };

  const onDragEnd = () => {
    setDragging(null);
    setHoverCell(null);
  };

  // Human-readable date range: "23. kolovoza – 19. rujna 2026". When the
  // range crosses year boundaries we duplicate the year on the left side too.
  const rangeLabel = (() => {
    if (!days.length) return "";
    const first = days[0];
    const last = days[days.length - 1];
    const opts = { day: "numeric", month: "long" };
    const leftYear = first.getFullYear();
    const rightYear = last.getFullYear();
    const leftHasYear = leftYear !== rightYear;
    const leftPart = first.toLocaleDateString("hr-HR", leftHasYear ? { ...opts, year: "numeric" } : opts);
    const rightPart = last.toLocaleDateString("hr-HR", { ...opts, year: "numeric" });
    return `${leftPart} – ${rightPart}`;
  })();
  const rangeLabelShort = (() => {
    if (!days.length) return "";
    const f = days[0];
    const l = days[days.length - 1];
    return `${f.getDate()}.${f.getMonth() + 1}. – ${l.getDate()}.${l.getMonth() + 1}.${String(l.getFullYear()).slice(2)}`;
  })();

  // Reusable unit-row renderer (right column). Extracted so we can group unit rows under
  // their Room Type instead of dumping them all at the bottom of the property.
  const renderUnitRow = (u) => (
    <div key={u.id} className="relative flex border-b border-border" style={{ height: ROW_HEIGHT, width: TOTAL_WIDTH }}>
      {days.map((d, i) => {
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const isHover = dragging && hoverCell?.unitId === u.id && hoverCell?.dayIdx === i;
        return (
          <div
            key={i}
            onMouseDown={(e) => { if (e.button === 0 && !dragging) { e.preventDefault(); startNewResDrag(u, d); } }}
            onMouseEnter={() => { if (newResDrag && newResDrag.unitId === u.id) extendNewResDrag(d); }}
            onClick={() => { if (!newResDrag) onDayCellClick(u, d); }}
            onDragOver={(e) => onCellDragOver(e, u, i)}
            onDrop={(e) => onCellDrop(e, u, d, i)}
            className={`shrink-0 cursor-pointer border-r border-border transition-colors hover:bg-primary/5 ${isWeekend ? "bg-muted/30" : ""} ${isHover ? "rw-cal-select" : ""} ${
              newResDrag && newResDrag.unitId === u.id && (() => {
                const lo = newResDrag.startISO < newResDrag.endISO ? newResDrag.startISO : newResDrag.endISO;
                const hi = newResDrag.startISO < newResDrag.endISO ? newResDrag.endISO : newResDrag.startISO;
                const iso = fmt(d);
                return lo === hi ? iso === lo : iso >= lo && iso < hi;
              })() ? "!bg-emerald-500/25 ring-1 ring-emerald-500" : newResDrag && newResDrag.unitId === u.id && newResDrag.startISO !== newResDrag.endISO && fmt(d) === (newResDrag.startISO < newResDrag.endISO ? newResDrag.endISO : newResDrag.startISO) ? "!bg-emerald-500/10 [background-image:linear-gradient(90deg,rgba(16,185,129,.25)_50%,transparent_50%)]" : ""
            }`}
            style={{ width: DAY_WIDTH }}
            data-testid={`cal-cell-${u.code}-${fmt(d)}`}
          />
        );
      })}
      {/* Reservation & block bars */}
      {(reservationsByUnit[u.id] || []).map((r) => {
        const block = blockFor(r);
        if (!block) return null;
        const isCancelled = r.status === "cancelled" || r.status === "no_show";
        const isUnitBlock = !!r.is_block;
        const isBlockDraft = isUnitBlock && !!r.is_draft;
        const color = r.color || reservationColor(r.source, tenant?.source_colors);
        const isDragSource = dragging?.reservation?.id === r.id;
        const isUnpaid = !isUnitBlock && r.payment_status !== "paid" && r.payment_status !== "refunded";
        const hasNote = !isUnitBlock && !!(r.notes && r.notes.trim());
        const openTasks = isUnitBlock ? 0 : (r.tasks || []).filter((t) => !t.done).length;
        const checkinPending = !isUnitBlock && r.status !== "checked_in" && r.status !== "checked_out" && r.status !== "cancelled" && r.status !== "no_show" && r.checkin_status !== "checked_in";
        const hasAnyIndicator = isUnpaid || hasNote || openTasks > 0 || checkinPending;
        // Block styling: diagonal grey stripes with dashed border for drafts,
        // solid muted grey for published blocks. Never looks like a guest bar.
        const blockBg = isUnitBlock
          ? "repeating-linear-gradient(45deg, rgba(100,116,139,0.55) 0 8px, rgba(148,163,184,0.45) 8px 16px)"
          : undefined;
        return (
          <div
            key={r.id}
            draggable
            onDragStart={(e) => onBlockDragStart(e, r, "move")}
            onDragEnd={onDragEnd}
            onClick={(e) => {
              e.stopPropagation();
              if (dragging) return;
              if (isUnitBlock) {
                setBlockPreview(r);
                return;
              }
              const rect = e.currentTarget.getBoundingClientRect();
              setQuickPreview({ reservation: r, anchorRect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height } });
            }}
            className={`group absolute flex items-center overflow-visible rounded-md text-left text-[11px] font-medium sm:text-[13px] shadow-sm transition-all hover:shadow-md cursor-grab active:cursor-grabbing ${isUnitBlock ? "text-slate-900" : "text-white"} ${isCancelled ? "opacity-50 line-through" : ""} ${isDragSource ? "opacity-40 ring-2 ring-primary" : ""} ${isBlockDraft ? "ring-2 ring-dashed ring-amber-500" : ""} ${moveConflicts.has(r.id) ? "rw-move-conflict" : r._moveDraft ? "rw-move-draft" : ""}`}
            style={{
              left: block.left + BLOCK_HORIZONTAL_GAP / 2,
              width: Math.max(block.width - BLOCK_HORIZONTAL_GAP, 40 - BLOCK_HORIZONTAL_GAP),
              top: BAR_TOP,
              height: BAR_HEIGHT,
              ...(moveConflicts.has(r.id) ? { top: r._moveDraft ? 2 : ROW_HEIGHT / 2 + 1, height: ROW_HEIGHT / 2 - 4 } : {}),
              background: blockBg || color,
              backgroundColor: isUnitBlock ? "#e2e8f0" : color,
              zIndex: 15,
            }}
            data-testid={isUnitBlock ? `cal-unit-block-${r.id}` : `cal-reservation-${r.code}`}
            data-move-draft={r._moveDraft ? "1" : undefined}
            data-move-conflict={moveConflicts.has(r.id) ? "1" : undefined}
            title={isUnitBlock
              ? `Blokada • ${r.block_reason || "bez razloga"} • ${r.check_in} → ${r.check_out}${isBlockDraft ? " • DRAFT — pošalji preko draft panela" : ""} • Povuci za pomicanje, rubove za promjenu duljine`
              : `${r.code} • ${r.primary_guest_name} • ${r.check_in} → ${r.check_out}${isUnpaid ? " • Neplaćeno" : ""}${hasNote ? " • Ima napomenu" : ""}${openTasks > 0 ? ` • ${openTasks} zadatak/aka` : ""}${checkinPending ? " • Check-in nije završen" : ""}`}
          >
            {/* Left resize handle — works for both reservations and blocks */}
            <div
              draggable
              onDragStart={(e) => onBlockDragStart(e, r, "resize-start")}
              onDragEnd={onDragEnd}
              onClick={(e) => e.stopPropagation()}
              className={`absolute left-0 top-0 h-full w-1.5 cursor-ew-resize rounded-l-md opacity-0 transition-opacity group-hover:opacity-100 ${isUnitBlock ? "bg-slate-600/50" : "bg-black/20"}`}
              title="Povuci za promjenu početnog datuma"
              data-testid={isUnitBlock ? `cal-block-resize-start-${r.id}` : `cal-res-resize-start-${r.code}`}
            />
            {!isUnitBlock ? (
              <StayName name={r.primary_guest_name} width={block.width} />
            ) : (
            <span className="flex items-center gap-1 truncate px-2 pb-0.5 leading-none">
              {isUnitBlock && <Lock size={11} weight="bold" className="shrink-0 text-slate-700" />}
              <span className="truncate">
                {isUnitBlock ? (r.block_reason || "Blokada") : r.primary_guest_name}
                {isBlockDraft && <span className="ml-1 rounded bg-amber-500/90 px-1 text-[9px] font-bold uppercase text-white">draft</span>}
              </span>
            </span>
            )}
            {hasAnyIndicator && (
              <div className="pointer-events-none absolute bottom-0 left-1.5 right-1.5 flex h-1 gap-0.5" data-testid={`cal-res-indicators-${r.code}`}>
                {isUnpaid && <span className="flex-1 rounded-sm bg-rose-500" data-testid={`cal-res-ind-unpaid-${r.code}`} />}
                {checkinPending && <span className="flex-1 rounded-sm bg-amber-500" data-testid={`cal-res-ind-checkin-${r.code}`} />}
                {hasNote && <span className="flex-1 rounded-sm bg-sky-500" data-testid={`cal-res-ind-note-${r.code}`} />}
                {openTasks > 0 && <span className="flex-1 rounded-sm bg-violet-500" data-testid={`cal-res-ind-tasks-${r.code}`} />}
              </div>
            )}
            {/* Right resize handle */}
            <div
              draggable
              onDragStart={(e) => onBlockDragStart(e, r, "resize-end")}
              onDragEnd={onDragEnd}
              onClick={(e) => e.stopPropagation()}
              className={`absolute right-0 top-0 h-full w-1.5 cursor-ew-resize rounded-r-md opacity-0 transition-opacity group-hover:opacity-100 ${isUnitBlock ? "bg-slate-600/50" : "bg-black/20"}`}
              title="Povuci za promjenu završnog datuma"
              data-testid={isUnitBlock ? `cal-block-resize-end-${r.id}` : `cal-res-resize-end-${r.code}`}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="rw-cal-dense space-y-3 overflow-hidden sm:space-y-4" data-testid="calendar-page">
      {/* Toolbar */}
      <div className="flex w-full flex-wrap items-center gap-2 sm:gap-3">
        <div className="flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            onClick={() => { const el = scrollRef.current; if (el && el.scrollLeft <= 1) { pendingScroll.current = "start"; sawLoading.current = false; setStartDate(addDays(startDate, -7)); } else el?.scrollBy({ left: -7 * DAY_WIDTH, behavior: "smooth" }); }}
            className="rounded-md border border-border bg-card p-2 text-foreground hover:bg-muted"
            data-testid="cal-prev"
          ><CaretLeft size={16} /></button>
          <button
            onClick={() => { autoScrollKey.current = ""; const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - CAL_LOOKBACK); setStartDate(d); if (scrollRef.current) scrollRef.current.scrollTo({ left: (CAL_LOOKBACK - 2) * DAY_WIDTH, behavior: "smooth" }); }}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
            data-testid="cal-today"
          >Danas</button>
          <button
            onClick={() => { const el = scrollRef.current; if (el && el.scrollLeft + el.clientWidth >= el.scrollWidth - DAY_WIDTH) { pendingScroll.current = "end"; sawLoading.current = false; setStartDate(addDays(startDate, 7)); } else el?.scrollBy({ left: 7 * DAY_WIDTH, behavior: "smooth" }); }}
            className="rounded-md border border-border bg-card p-2 text-foreground hover:bg-muted"
            data-testid="cal-next"
          ><CaretRight size={16} /></button>
          <span className="ml-1 truncate whitespace-nowrap text-sm font-medium text-muted-foreground sm:ml-2" data-testid="cal-range-label">
            <span className="sm:hidden">{rangeLabelShort}</span>
            <span className="hidden sm:inline">{rangeLabel}</span>
          </span>
        </div>
        {/* View range presets */}
        <div className="hidden shrink-0 items-center gap-1 rounded-md border border-border bg-card p-0.5 md:flex" data-testid="cal-range-presets">
          {CAL_AHEAD_OPTIONS.map((p) => (
            <button
              key={p.n}
              onClick={() => { setAheadDays(p.n); try { localStorage.setItem("rw-cal-ahead", String(p.n)); } catch { /* ignore */ } }}
              className={`whitespace-nowrap rounded px-2.5 py-1 text-xs font-medium transition ${
                aheadDays === p.n ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              }`}
              data-testid={`cal-range-${p.n}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-0.5 rounded-md border border-border bg-card p-0.5" data-testid="cal-zoom-toggle">
          {[{ z: "compact", label: "Kompaktno", icon: <MagnifyingGlassMinus size={14} /> }, { z: "wide", label: "Široko", icon: <MagnifyingGlassPlus size={14} /> }].map((o) => (
            <button
              key={o.z}
              type="button"
              onClick={() => changeZoom(o.z)}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition ${calZoom === o.z ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
              title={o.z === "wide" ? "Šire ćelije — vidi se cijelo ime gosta" : "Uže ćelije — više dana odjednom"}
              data-testid={`cal-zoom-${o.z}`}
            >
              {o.icon}<span className="hidden lg:inline">{o.label}</span>
            </button>
          ))}
        </div>
        <div className="shrink-0"><CalendarLegend /></div>
        {/* Input mode toggle — drag-out creates a reservation (default) or a
            draft unit-block (grey stripes). Blocks reduce availability without
            looking like a guest booking. Persisted in localStorage. */}
        <div className="flex shrink-0 items-center gap-1 rounded-md border border-border bg-muted/40 p-0.5" data-testid="cal-input-mode-toggle">
          <button
            type="button"
            onClick={() => setInputMode("reservation")}
            className={`flex items-center justify-center gap-1 whitespace-nowrap rounded px-2.5 py-1 text-xs font-medium transition ${inputMode === "reservation" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted"}`}
            title="Povlačenjem stvaraš rezervaciju s gostom"
            data-testid="cal-input-mode-reservation"
          >
            <Bed size={13} weight="bold" /> Rezervacije
          </button>
          <button
            type="button"
            onClick={() => setInputMode("block")}
            className={`flex items-center justify-center gap-1 whitespace-nowrap rounded px-2.5 py-1 text-xs font-medium transition ${inputMode === "block" ? "bg-slate-700 text-white shadow-sm" : "text-muted-foreground hover:bg-muted"}`}
            title="Povlačenjem stvaraš blokadu jedinice (draft) — pošalje se s draftom"
            data-testid="cal-input-mode-block"
          >
            <Lock size={13} weight="bold" /> Blokade
          </button>
        </div>
        <button
          onClick={() => (inputMode === "block" ? setBlockDialog(true) : setDrawer({ type: "new" }))}
          className={`ml-auto flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold ${inputMode === "block" ? "bg-slate-700 text-white hover:bg-slate-800" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
          data-testid="cal-new-reservation"
        >
          <Plus size={16} weight="bold" />
          {inputMode === "block"
            ? <><span className="hidden whitespace-nowrap sm:inline">Nova blokada</span><span className="sm:hidden">Nova</span></>
            : <><span className="hidden whitespace-nowrap sm:inline">Nova rezervacija</span><span className="sm:hidden">Nova</span></>}
        </button>
      </div>

      {/* Timeline */}
      <div ref={scrollRef} onScroll={onGridScroll} className="overflow-auto overscroll-contain rounded-lg border border-border bg-card" style={{ scrollbarGutter: "stable", maxHeight: gridMaxH || "calc(100dvh - 220px)" }} data-testid="cal-scroll-container">
        {loading ? (
          <div className="flex h-96 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>
        ) : (
          <div className="relative flex" style={{ width: labelsWidth + TOTAL_WIDTH }} ref={containerRef}>
            {/* Continuous "Danas" vertical overlay — a single outlined column-wide box behind reservations & cells */}
            {todayCol >= 0 && (
              <div
                aria-hidden
                className="pointer-events-none absolute rounded-sm"
                style={{
                  left: labelsWidth + todayCol * DAY_WIDTH,
                  top: 0,
                  bottom: 0,
                  width: DAY_WIDTH,
                  zIndex: 5,
                  background: "hsl(var(--primary) / 0.06)",
                  border: "1.5px solid hsl(var(--primary))",
                  boxShadow: "0 0 0 1px hsl(var(--primary) / 0.15)",
                }}
                data-testid="cal-today-overlay"
              />
            )}
            {/* Month boundary lines (1st of each month) */}
            {days.map((d, i) => (i > 0 && d.getDate() === 1 ? (
              <div
                key={`mb-${i}`}
                aria-hidden
                className="pointer-events-none absolute top-0 bottom-0"
                style={{ left: labelsWidth + i * DAY_WIDTH - 1, width: 2, zIndex: 4, background: "hsl(var(--primary) / 0.45)" }}
                data-testid={`cal-month-boundary-${fmt(d)}`}
              />
            ) : null))}
            {/* Left: unit list (sticky-left so it stays visible when scrolling horizontally)
                z-40 keeps it above all right-column rows (property bar / rate bar / day cells)
                which use z-20/z-30; without this, scrolling right makes the dark property bar
                and day headers slide over the sidebar labels. */}
            <div
              className="sticky left-0 z-40 shrink-0 border-r border-border bg-card"
              style={{ width: labelsWidth }}
              data-testid="cal-units-column"
            >
              <div className="sticky top-0 z-30 flex h-12 flex-col justify-center border-b border-border bg-card px-3 leading-tight">
                <span className="text-sm font-bold text-primary" data-testid="cal-visible-month">{visibleMonthLabel}</span>
                <span className="text-[10px] font-semibold uppercase text-muted-foreground">Jedinica</span>
              </div>
              {grouped.map((g) => {
                const collapsed = collapsedProperties.has(g.property.id);
                return (
                  <div key={g.property.id}>
                    <button
                      type="button"
                      onClick={() => toggleProperty(g.property.id)}
                      className="relative z-20 flex w-full items-center gap-1.5 border-b border-border bg-foreground/90 px-2 text-left text-[10px] font-medium uppercase tracking-wide sm:px-3 sm:text-[11px] sm:tracking-wider text-background hover:bg-foreground"
                      style={{ height: INFO_ROW_HEIGHT }}
                      data-testid={`cal-property-toggle-${g.property.id}`}
                    >
                      {collapsed ? <CaretRight size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
                      <span className="truncate">{g.property.name}</span>
                    </button>
                    {!collapsed && (
                      <>
                        <div
                          className="flex items-center border-b border-border bg-muted/60 px-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:px-3 sm:text-[11px]"
                          style={{ height: INFO_ROW_HEIGHT }}
                          data-testid={`cal-vacant-label-${g.property.id}`}
                        >
                          Slobodno
                        </div>
                        {/* Room-type block: header, EACH rate plan is its OWN independent
                            collapsible row (BAR + all derived plans are peers with own chevron
                            that expands ONLY its own restriction rows). */}
                        {roomTypesForGroup(g.units).map((rti) => {
                          const rtUnitIdsSet = new Set(rti.unitIds);
                          const rtUnits = g.units.filter((u) => rtUnitIdsSet.has(u.id));
                          const anyRpExpanded = rti.ratePlans.some((rp) => expandedRestrictions.has(`rp:${rp.id}`));
                          return (
                          <div key={`rt-${rti.rt.id}`}>
                            <div
                              className="flex items-center border-b border-border bg-primary/10 px-2 text-[10.5px] font-normal text-primary sm:px-3 sm:text-[11px]"
                              style={{ height: INFO_ROW_HEIGHT - 4 }}
                              data-testid={`cal-rt-header-${rti.rt.id}`}
                            >
                              <Bed size={11} weight="duotone" className="mr-1 shrink-0" />
                              <span className="truncate">{rti.rt.name}</span>
                              <span className="ml-1 shrink-0 rounded-full bg-primary/15 px-1.5 py-px text-[9px] font-bold text-primary sm:ml-2">{rti.unitIds.length}</span>
                            </div>
                            {visibleRatePlans(rti).map((rp) => {
                              const rpExpanded = expandedRestrictions.has(`rp:${rp.id}`);
                              // Restrictions specific to THIS rate plan; each plan has its own toggle.
                              const visibleRestrictions = RESTRICTION_ROWS.filter((r) => pinnedRows.has(`res:${rp.id}:${r.key}`) || rpExpanded);
                              return (
                              <React.Fragment key={`rp-block-${rp.id}`}>
                              <div
                                key={`rp-${rp.id}`}
                                onClick={(e) => { e.stopPropagation(); toggleRestrictions(`rp:${rp.id}`); }}
                                className={`flex items-center border-b border-border cursor-pointer select-none hover:bg-muted/50 pr-1 sm:pr-3 text-[10.5px] sm:text-[11px] ${rp.is_default ? "bg-card pl-1 sm:pl-3 font-normal text-foreground" : "bg-muted/10 pl-3 sm:pl-7 font-normal text-foreground/80"}`}
                                title={rp.name}
                                style={{ height: INFO_ROW_HEIGHT - 4 }}
                                data-testid={`cal-rp-label-${rti.rt.id}-${rp.id}`}
                              >
                                <span
                                  className="mr-0.5 flex h-5 w-4 shrink-0 items-center justify-center rounded text-foreground/70 sm:mr-1.5 sm:h-4 sm:w-4 sm:text-muted-foreground"
                                  data-testid={`cal-rp-expand-toggle-${rp.id}`}
                                  title={rpExpanded ? "Sakrij restrikcije" : "Prikaži restrikcije"}
                                >
                                  {rpExpanded ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
                                </span>
                                <span className="min-w-0 flex-1 truncate">
                                  {rp.is_default && <span className="mr-0.5 text-primary sm:mr-1">★</span>}
                                  {narrowLabels ? (rp.code || rp.name) : rp.name}
                                </span>
                                {!rp.is_default && rp.price_modifier_mode !== "same" && (
                                  <span className="mr-0.5 shrink-0 text-[9px] font-mono text-muted-foreground sm:mr-1 sm:text-muted-foreground/70">
                                    {rp.price_modifier_mode === "fixed" && `${rp.price_modifier_value >= 0 ? "+" : ""}${rp.price_modifier_value}€`}
                                    {rp.price_modifier_mode === "markup" && `+${rp.price_modifier_value}%`}
                                    {rp.price_modifier_mode === "markdown" && `-${rp.price_modifier_value}%`}
                                  </span>
                                )}
                                {rp.is_default && rti.ratePlans.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleDerived(rti.rt.id); }}
                                    className={`ml-0.5 inline-flex shrink-0 items-center gap-0.5 rounded-full border px-1.5 py-px text-[9px] font-bold leading-[14px] transition sm:ml-1 ${expandedDerived.has(rti.rt.id) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground hover:border-primary hover:text-primary"}`}
                                    title={expandedDerived.has(rti.rt.id) ? "Sakrij izvedene rate-ove" : "Prikaži izvedene rate-ove"}
                                    data-testid={`cal-derived-toggle-${rti.rt.id}`}
                                  >
                                    {expandedDerived.has(rti.rt.id) ? <CaretDown size={8} weight="bold" /> : <CaretRight size={8} weight="bold" />}
                                    +{rti.ratePlans.length - 1}
                                  </button>
                                )}
                              </div>
                              {/* Per-rate-plan restriction label rows — nested under this rate plan. */}
                              {visibleRestrictions.map((r) => {
                                const isPinned = pinnedRows.has(`res:${rp.id}:${r.key}`);
                                return (
                                  <div
                                    key={`res-${rti.rt.id}-${rp.id}-${r.key}`}
                                    className={`flex items-center border-b border-border ${isPinned ? "bg-muted/15" : "bg-muted/25"} ${narrowLabels ? "pl-5 pr-0.5" : "pl-12 pr-3"} text-[10px] sm:text-[10.5px] font-medium text-foreground/70 sm:text-muted-foreground`}
                                    title={r.fullLabel}
                                    style={{ height: INFO_ROW_HEIGHT - 4 }}
                                    data-testid={`cal-restriction-label-${rti.rt.id}-${rp.id}-${r.key}`}
                                  >
                                    <span className="min-w-0 flex-1 truncate">{narrowLabels ? r.short : r.label}</span>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); togglePin(`res:${rp.id}:${r.key}`); }}
                                      className={`ml-0.5 shrink-0 rounded p-0.5 transition ${isPinned ? "text-primary" : "text-muted-foreground/40 hover:text-primary"}`}
                                      title={isPinned ? "Odpini iz uvijek-vidljivih" : "Pini u uvijek-vidljive"}
                                      data-testid={`cal-pin-res-${rti.rt.id}-${rp.id}-${r.key}`}
                                    >
                                      <PushPin size={isMobile ? 10 : 11} weight={isPinned ? "fill" : "regular"} />
                                    </button>
                                  </div>
                                );
                              })}
                              </React.Fragment>
                              );
                            })}
                            {/* Channel rows removed per user request — OTA per-channel prices are not used in this UI */}
                            {/* Units belonging to this RT — grouped immediately under their room type */}
                            {rtUnits.map((u) => (
                              <div
                                key={u.id}
                                className="flex items-center gap-2 border-b border-border px-2 sm:px-3"
                                style={{ height: ROW_HEIGHT }}
                                title={u.code}
                                data-testid={`cal-unit-${u.code}`}
                              >
                                <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                                  <p className={`truncate text-[12px] font-medium leading-tight sm:text-xs ${u.active === false ? "text-muted-foreground line-through" : "text-foreground"}`}>{u.code}</p>
                                  {u.active === false && <span className="shrink-0 rounded bg-amber-500/15 px-1 text-[9px] font-bold uppercase text-amber-700 dark:text-amber-300" title="Jedinica je uklonjena, ali još ima aktivnu rezervaciju — premjesti je">Uklonjena</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                          );
                        })}
                        {/* Units WITHOUT a room type — legacy fallback, listed last */}
                        {g.units.filter((u) => !u.room_type_id).map((u) => (
                          <div
                            key={u.id}
                            className="flex items-center gap-2 border-b border-border px-2 sm:px-3"
                            style={{ height: ROW_HEIGHT }}
                            title={u.code}
                            data-testid={`cal-unit-${u.code}`}
                          >
                            <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                              <p className={`truncate text-[12px] font-semibold leading-tight sm:text-sm ${u.active === false ? "text-muted-foreground line-through" : "text-foreground"}`}>{u.code}</p>
                              {u.active === false && <span className="shrink-0 rounded bg-amber-500/15 px-1 text-[9px] font-bold uppercase text-amber-700 dark:text-amber-300" title="Jedinica je uklonjena, ali još ima aktivnu rezervaciju — premjesti je">Uklonjena</span>}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Right: timeline (fixed width, horizontal scroll handled by outer container) */}
            <div className="relative shrink-0" style={{ width: TOTAL_WIDTH }}>
              {/* Day header */}
              <div ref={dayHeaderRowRef} className="sticky top-0 z-30 flex h-12 border-b border-border bg-card" style={{ width: TOTAL_WIDTH }} data-testid="cal-day-header">
                {days.map((d, i) => {
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  const isToday = i === todayCol;
                  return (
                    <div
                      key={i}
                      data-day-cell="1"
                      className={`flex shrink-0 flex-col items-center justify-center border-r border-border ${isWeekend ? "bg-muted/40" : ""} ${isToday ? "border-t-2 border-b-2 border-t-primary border-b-primary" : ""}`}
                      style={{ width: DAY_WIDTH, ...(isToday ? { background: "hsl(var(--primary) / 0.06)", boxShadow: "inset 1.5px 0 0 0 hsl(var(--primary)), inset -1.5px 0 0 0 hsl(var(--primary))" } : {}) }}
                    >
                      <span className={`text-[10px] uppercase ${isToday ? "text-primary font-bold" : "text-muted-foreground"}`}>
                        {d.toLocaleDateString("hr-HR", { weekday: "short" })}
                      </span>
                      <span className={`text-xs font-medium leading-tight ${isToday ? "text-primary" : "text-foreground"}`}>
                        {d.getDate()}
                      </span>
                      {d.getDate() === 1 && (
                        <span className="text-[9px] font-bold uppercase leading-none text-primary" data-testid={`cal-month-start-${fmt(d)}`}>
                          {d.toLocaleDateString("hr-HR", { month: "short" }).replace(".", "")}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Rows */}
              {grouped.map((g) => {
                const collapsed = collapsedProperties.has(g.property.id);
                return (
                  <div key={g.property.id} style={{ width: TOTAL_WIDTH }}>
                    {/* Property header strip (visual match with left column) */}
                    <div className="relative z-20 flex border-b border-border bg-foreground/90" style={{ width: TOTAL_WIDTH, height: INFO_ROW_HEIGHT }}>
                      {days.map((d, i) => {
                        const isToday = i === todayCol;
                        return (
                          <div
                            key={i}
                            className={`shrink-0 border-r border-foreground/40 ${isToday ? "bg-primary/30" : ""}`}
                            style={{ width: DAY_WIDTH }}
                          />
                        );
                      })}
                    </div>

                    {!collapsed && (
                      <>
                        {/* Vacant units row */}
                        <div className="flex border-b border-border bg-muted/60" style={{ width: TOTAL_WIDTH, height: INFO_ROW_HEIGHT }}>
                          {days.map((d, i) => {
                            const iso = fmt(d);
                            const cnt = vacantUnitsForDate(g.units, iso);
                            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                            const isToday = i === todayCol;
                            const totalUnits = g.units.length;
                            const ratio = totalUnits > 0 ? cnt / totalUnits : 0;
                            // Smooth colour gradient: full → dark ink, empty → red.
                            // Uses HSL interpolation from foreground (dark slate) at 100% availability
                            // to a saturated rose at 0%.
                            const vacantColor = (() => {
                              if (totalUnits === 0) return undefined;
                              // 0..1 clamp
                              const r = Math.max(0, Math.min(1, ratio));
                              // Hue: 220 (dark blue-slate) → 0 (red)
                              const hue = Math.round(220 * r);
                              // Saturation: 15% when full → 78% when empty
                              const sat = Math.round(15 + (1 - r) * 63);
                              // Lightness: 30% when full → 48% when empty (pop the red)
                              const light = Math.round(30 + (1 - r) * 18);
                              return `hsl(${hue}, ${sat}%, ${light}%)`;
                            })();
                            return (
                              <div
                                key={i}
                                className={`flex shrink-0 items-center justify-center border-r border-border ${isWeekend ? "bg-muted/80" : ""} ${isToday ? "bg-primary/10" : ""}`}
                                style={{ width: DAY_WIDTH }}
                                title={`${cnt} od ${totalUnits} slobodno`}
                              >
                                <span style={{ fontSize: IS_PHONE ? "11px" : "11px", fontWeight: 500, color: vacantColor }} data-testid={`cal-vacant-${g.property.id}-${iso}`}>{cnt}</span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Room-type block: RT header, rate plan rows (each independently
                            collapsible), then channels + restrictions per rate plan */}
                        {roomTypesForGroup(g.units).map((rti) => {
                          const rtUnitIdsSet = new Set(rti.unitIds);
                          const rtUnits = g.units.filter((u) => rtUnitIdsSet.has(u.id));
                          const anyRpExpanded = rti.ratePlans.some((rp) => expandedRestrictions.has(`rp:${rp.id}`));
                          return (
                          <div key={`rtc-${rti.rt.id}`}>
                            {/* Empty RT header row on right side (colored strip matching left) */}
                            <div className="flex border-b border-border bg-primary/10" style={{ width: TOTAL_WIDTH, height: INFO_ROW_HEIGHT - 4 }}>
                              {days.map((d, i) => {
                                const isToday = i === todayCol;
                                return <div key={i} className={`shrink-0 border-r border-border/40 ${isToday ? "bg-primary/20" : ""}`} style={{ width: DAY_WIDTH }} />;
                              })}
                            </div>
                            {visibleRatePlans(rti).map((rp) => {
                              const rpExpanded = expandedRestrictions.has(`rp:${rp.id}`);
                              const visibleRestrictions = RESTRICTION_ROWS.filter((r) => pinnedRows.has(`res:${rp.id}:${r.key}`) || rpExpanded);
                              return (
                              <React.Fragment key={`rpc-block-${rp.id}`}>
                              <div
                                key={`rpc-${rp.id}`}
                                className="flex border-b border-border bg-card"
                                style={{ width: TOTAL_WIDTH, height: INFO_ROW_HEIGHT - 4 }}
                                data-testid={`cal-rp-row-${rti.rt.id}-${rp.id}`}
                              >
                                {days.map((d, i) => {
                                  const iso = fmt(d);
                                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                  const isToday = i === todayCol;
                                  const isPast = iso < todayISO;  // F6.13 — lock past cells
                                  const price = priceForRoomTypeRatePlan(rti.rt, rp, rti.unitIds, iso);
                                  const isClosed = price == null || price <= 0;
                                  // Draft overlay: if user has an unsent price
                                  // change for this cell, show that value instead
                                  // with an amber dashed border so the operator
                                  // can see WHERE their pending changes land.
                                  const draftVal = draftValueFor(rti.rt.id, rp.id, "rate_plan_base_price", iso);
                                  const hasDraft = draftVal !== undefined;
                                  const displayPrice = hasDraft ? Number(draftVal) : (price ?? 0);
                                  // F6.13 — Past dates are read-only to protect
                                  // issued invoices. All other cells remain editable.
                                  const editable = !isPast;
                                  const onCellMouseDown = editable ? (ev) => {
                                    ev.preventDefault();
                                    const rect = ev.currentTarget.getBoundingClientRect();
                                    setRateDrag({
                                      propertyId: g.property.id,
                                      propertyName: g.property.name,
                                      unitIds: rti.unitIds,
                                      field: "rate_plan_base_price",
                                      roomTypeId: rti.rt.id,
                                      roomTypeName: rti.rt.name,
                                      ratePlanId: rp.id,
                                      ratePlanName: rp.name,
                                      startISO: iso,
                                      endISO: iso,
                                      startRect: rect,
                                      endRect: rect,
                                      currentValue: price,
                                    });
                                  } : undefined;
                                  const onCellMouseEnter = editable ? () => {
                                    if (rateDrag && rateDrag.field === "rate_plan_base_price" && rateDrag.ratePlanId === rp.id) {
                                      const el = document.querySelector(`[data-testid="cal-rp-price-${rti.rt.id}-${rp.id}-${iso}"]`);
                                      setRateDrag((d) => d ? { ...d, endISO: iso, endRect: el?.getBoundingClientRect() || d.endRect } : d);
                                    }
                                  } : undefined;
                                  const isInDrag = rateDrag && rateDrag.field === "rate_plan_base_price" && rateDrag.ratePlanId === rp.id
                                    && iso >= (rateDrag.startISO < rateDrag.endISO ? rateDrag.startISO : rateDrag.endISO)
                                    && iso <= (rateDrag.startISO < rateDrag.endISO ? rateDrag.endISO : rateDrag.startISO);
                                  return (
                                    <div
                                      key={i}
                                      onMouseDown={onCellMouseDown}
                                      onMouseEnter={onCellMouseEnter}
                                      className={`relative flex shrink-0 items-center justify-center border-r border-border ${isWeekend ? "bg-muted/40" : ""} ${isToday ? "bg-primary/5" : ""} ${isPast ? "cursor-not-allowed bg-muted/70 opacity-50" : (editable ? "cursor-pointer hover:bg-primary/20" : "")} ${isInDrag ? "!bg-primary/25" : ""} ${hasDraft ? "!bg-amber-100/70 ring-1 ring-inset ring-dashed ring-amber-500" : (isClosed && !hasDraft && !isPast ? "cal-closed-cell" : "")}`}
                                      style={{ width: DAY_WIDTH }}
                                      title={isPast ? `🔒 Povijesni datum — cijene su zaključane radi integriteta računa.` : hasDraft ? `DRAFT: ${displayPrice} € (nespremljeno)` : isClosed ? `Nema cijene — datum nije otvoren za prodaju. Klikni ili povuci da postaviš period cijena.` : editable ? `Klik ili povuci za uređivanje · ${rti.rt.name} · ★ ${rp.name} · ${iso}` : `Automatski iz ★ ${rp.price_modifier_mode === "fixed" ? `+${rp.price_modifier_value}€` : rp.price_modifier_mode !== "same" ? `${rp.price_modifier_mode === "markup" ? "+" : "-"}${rp.price_modifier_value}%` : ""}`}
                                      data-testid={`cal-rp-price-${rti.rt.id}-${rp.id}-${iso}`}
                                    >
                                      <span className={`text-[11px] ${hasDraft ? "font-normal text-amber-700" : isClosed ? "font-normal text-muted-foreground/50" : rp.is_default ? "font-normal text-primary" : "font-normal text-muted-foreground/80"}`}>
                                        {hasDraft && displayPrice > 0 ? Number(displayPrice).toFixed(0) : isClosed ? "—" : Number(displayPrice).toFixed(0)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                              {/* Per-rate-plan restriction cells — scoped to this rate plan.
                                  Click sends a draft with ratePlanId=rp.id so backend stores a
                                  rate_plan_id-scoped RatePeriod override (needed for Channex cert
                                  Test #5-#7 where each rate has its own min_stay). */}
                              {visibleRestrictions.map((r) => (
                                <div
                                  key={`resc-${rti.rt.id}-${rp.id}-${r.key}`}
                                  className={`flex border-b border-border ${pinnedRows.has(`res:${rp.id}:${r.key}`) ? "bg-muted/15" : "bg-muted/25"}`}
                                  style={{ width: TOTAL_WIDTH, height: INFO_ROW_HEIGHT - 4 }}
                                  data-testid={`cal-restriction-row-${rti.rt.id}-${rp.id}-${r.key}`}
                                >
                                  {days.map((d, i) => {
                                    const iso = fmt(d);
                                    const savedVal = restrictionForRatePlanOnDate(rtUnitIdsSet, iso, r.key, rp.id);
                                    const draftV = draftValueFor(rti.rt.id, rp.id, r.key, iso);
                                    const hasDraft = draftV !== undefined;
                                    const val = hasDraft ? draftV : savedVal;
                                    const period = findPeriodForRatePlan(rtUnitIdsSet, iso, rp.id);
                                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                    const isToday = i === todayCol;
                                    const isPast = iso < todayISO;  // F6.13 — lock past cells
                                    let display = "·";
                                    let pillClass = "";
                                    let textClass = "text-muted-foreground/40";
                                    let isSet = false;
                                    if (r.boolean) {
                                      if (val === true) {
                                        display = "✓";
                                        isSet = true;
                                        pillClass = r.key === "stop_sell"
                                          ? "bg-rose-600 text-white shadow-sm"
                                          : "bg-amber-500 text-white shadow-sm";
                                        textClass = "";
                                      }
                                    } else {
                                      const shown = r.fmt ? r.fmt(val) : (val ?? "");
                                      if (shown) {
                                        display = shown;
                                        isSet = true;
                                        pillClass = "bg-primary text-primary-foreground shadow-sm";
                                        textClass = "font-mono font-bold";
                                      }
                                    }
                                    // Draft overrides — show amber outline + amber pill so operator
                                    // can spot pending restriction changes at a glance.
                                    if (hasDraft) {
                                      pillClass = "bg-amber-500 text-white shadow-sm ring-2 ring-amber-300";
                                      textClass = r.boolean ? "" : "font-mono font-bold";
                                    }
                                    const isInDrag = rateDrag
                                      && rateDrag.field === r.key
                                      && rateDrag.roomTypeId === rti.rt.id
                                      && rateDrag.ratePlanId === rp.id
                                      && iso >= (rateDrag.startISO < rateDrag.endISO ? rateDrag.startISO : rateDrag.endISO)
                                      && iso <= (rateDrag.startISO < rateDrag.endISO ? rateDrag.endISO : rateDrag.startISO);
                                    return (
                                      <button
                                        type="button"
                                        key={i}
                                        disabled={isPast}
                                        onMouseDown={isPast ? undefined : (ev) => {
                                          ev.preventDefault();
                                          const rect = ev.currentTarget.getBoundingClientRect();
                                          setRateDrag({
                                            propertyId: g.property.id,
                                            propertyName: g.property.name,
                                            unitIds: rti.unitIds,
                                            field: r.key,
                                            roomTypeId: rti.rt.id,
                                            roomTypeName: rti.rt.name,
                                            ratePlanId: rp.id,
                                            ratePlanName: rp.name,
                                            startISO: iso,
                                            endISO: iso,
                                            startRect: rect,
                                            endRect: rect,
                                            currentValue: val,
                                          });
                                        }}
                                        onMouseEnter={isPast ? undefined : () => {
                                          if (rateDrag && rateDrag.field === r.key && rateDrag.roomTypeId === rti.rt.id && rateDrag.ratePlanId === rp.id) {
                                            const el = document.querySelector(`[data-testid="cal-restriction-cell-${rti.rt.id}-${rp.id}-${r.key}-${iso}"]`);
                                            setRateDrag((d) => d ? { ...d, endISO: iso, endRect: el?.getBoundingClientRect() || d.endRect } : d);
                                          }
                                        }}
                                        className={`flex shrink-0 items-center justify-center border-r border-border transition ${isPast ? "cursor-not-allowed bg-muted/70 opacity-50" : "hover:bg-primary/10"} ${isWeekend && !isPast ? "bg-muted/50" : ""} ${isToday ? "bg-primary/10" : ""} ${isInDrag ? "!bg-primary/25" : ""} ${hasDraft ? "!bg-amber-100/70 ring-1 ring-inset ring-dashed ring-amber-500" : ""}`}
                                        style={{ width: DAY_WIDTH }}
                                        title={isPast ? `🔒 Povijesni datum — zaključano` : hasDraft ? `DRAFT (nespremljeno): ${r.fullLabel || r.label} = ${r.boolean ? (val ? "Da" : "Ne") : val}` : period ? `${r.fullLabel || r.label} · ${rp.name} (${period.name}) — kliknite ili povucite` : `${r.fullLabel || r.label} · ${rp.name} — kliknite ili povucite`}
                                        data-testid={`cal-restriction-cell-${rti.rt.id}-${rp.id}-${r.key}-${iso}`}
                                      >
                                        {isSet ? (
                                          <span
                                            className={`inline-flex min-w-[18px] items-center justify-center rounded px-1 py-px text-[10px] leading-[14px] sm:min-w-[22px] sm:py-0.5 sm:text-[11px] sm:leading-none ${pillClass} ${textClass}`}
                                            data-restriction-set="1"
                                          >
                                            {display}
                                          </span>
                                        ) : (
                                          <span className={`text-[13px] ${textClass}`}>{display}</span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              ))}
                              </React.Fragment>
                              );
                            })}
                            {/* Channel rate rows removed — OTA per-channel prices not used in UI */}
                            {/* Unit rows for this RT — grouped immediately under their room type */}
                            {rtUnits.map((u) => renderUnitRow(u))}
                          </div>
                          );
                        })}

                        {/* Units WITHOUT a room type — legacy fallback, listed last */}
                        {g.units.filter((u) => !u.room_type_id).map((u) => renderUnitRow(u))}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {drawer && (
        <ReservationDrawer
          reservation={drawer.reservation}
          units={units}
          properties={properties}
          initialDates={drawer.initialDates}
          onClose={() => setDrawer(null)}
          onSaved={(saved) => { onReservationSaved(saved); if (moveDrafts[saved.id]) removeMoveDraft(saved.id); }}
          onDeleted={(id) => { onReservationDeleted(id); removeMoveDraft(id); }}
          onMoveConflict={(r, target, payload) => setMoveConfirm({
            reservation: r, target, clash: findUnitConflict(r.id, target.unit_id, target.check_in, target.check_out),
            afterConfirm: async () => {
              // save the non-move fields now; the move itself waits in the draft
              const rest = { ...payload, unit_id: r.unit_id, property_id: r.property_id, check_in: r.check_in, check_out: r.check_out };
              try { const { data } = await api.put(`/reservations/${r.id}`, rest); onReservationSaved(data); } catch (err) { toast.error(formatApiError(err)); }
              setDrawer(null);
            },
          })}
        />
      )}

      {blockDialog && (
        <BlockDialog
          groups={grouped}
          onClose={() => setBlockDialog(false)}
          onCreated={(blocks) => { setReservations((rs) => [...rs, ...blocks]); setBlockDialog(false); }}
        />
      )}
      {blockPreview && (
        <div className="fixed inset-0 z-40" onClick={() => setBlockPreview(null)} data-testid="block-preview-overlay">
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          <div
            className="fixed left-1/2 top-1/2 z-50 w-[420px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="block-preview"
          >
            <div className="mb-3 flex items-center gap-2">
              <Lock size={18} weight="fill" className="text-slate-600" />
              <h3 className="text-base font-semibold text-foreground">Blokada jedinice</h3>
              {blockPreview.is_draft && <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">draft</span>}
            </div>
            <div className="space-y-1.5 text-sm">
              <p><span className="text-muted-foreground">Jedinica:</span> <span className="font-mono font-semibold text-foreground">{units.find((u) => u.id === blockPreview.unit_id)?.code || blockPreview.unit_id}</span></p>
              <p><span className="text-muted-foreground">Razdoblje:</span> <span className="font-mono text-foreground">{blockPreview.check_in} → {blockPreview.check_out}</span></p>
              <p><span className="text-muted-foreground">Razlog:</span> <span className="text-foreground">{blockPreview.block_reason || "—"}</span></p>
              {blockPreview.is_draft && (
                <p className="mt-2 rounded bg-amber-500/10 p-2 text-xs text-amber-800">
                  Ova blokada je još u draftu — pošalji preko "Objavi drafts" da bi se sinkronizirala dostupnost.
                </p>
              )}
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setBlockPreview(null)}
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
                data-testid="block-preview-close"
              >
                Zatvori
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.delete(`/unit-blocks/${blockPreview.id}`);
                    setReservations((rs) => rs.filter((r) => r.id !== blockPreview.id));
                    setBlockPreview(null);
                    toast.success("Blokada uklonjena");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Greška pri brisanju");
                  }
                }}
                className="flex-1 rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                data-testid="block-preview-delete"
              >
                <Trash size={14} weight="bold" className="mr-1 inline" /> Ukloni blokadu
              </button>
            </div>
          </div>
        </div>
      )}

      {quickPreview && (
        <QuickReservationPreview
          reservation={quickPreview.reservation}
          anchorRect={quickPreview.anchorRect}
          onClose={() => setQuickPreview(null)}
          onOpenFull={() => { setDrawer({ type: "edit", reservation: reservations.find((x) => x.id === quickPreview.reservation.id) || quickPreview.reservation }); setQuickPreview(null); }}
          onOpenGuest={(gid) => { setCalGuestId(gid); setQuickPreview(null); }}
          onCheckIn={async (undo = false) => {
            try {
              const { data } = await api.put(`/reservations/${quickPreview.reservation.id}`, { status: undo ? "confirmed" : "checked_in" });
              onReservationSaved(data);
              setQuickPreview((q) => (q ? { ...q, reservation: { ...q.reservation, ...data } } : q));
              toast.success(undo ? "Check-in poništen" : "Gost je checked-in");
              return true;
            } catch (e) {
              toast.error(undo ? "Greška pri poništavanju check-ina" : "Greška pri check-inu");
              return false;
            }
          }}
          onTogglePaid={async (next) => {
            try {
              const { data } = await api.put(`/reservations/${quickPreview.reservation.id}`, { payment_status: next ? "paid" : "unpaid" });
              onReservationSaved(data);
              setQuickPreview((qp) => qp ? { ...qp, reservation: data } : qp);
            } catch (e) {
              toast.error("Greška");
            }
          }}
        />
      )}

      {editor && (
        <CellEditor
          state={editor}
          roomTypeOptions={editorRoomTypeOptions}
          onClose={() => setEditor(null)}
          onDraft={(list) => {
            list.forEach((d) => addDraft(d, true));
            toast.success(list.length > 1 ? `Dodano ${list.length} promjena u draft` : "Dodano u draft — pošalji sve odjednom kad završiš");
            setEditor(null);
          }}
        />
      )}

      {dragError && (
        <div className="fixed bottom-20 right-4 z-50 flex max-w-sm lg:bottom-4 items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 shadow-lg" data-testid="drag-error-toast">
          <Warning size={18} weight="duotone" className="mt-0.5 shrink-0 text-destructive" />
          <div className="flex-1 text-sm text-destructive">{dragError}</div>
          <button onClick={() => setDragError("")} className="text-destructive hover:opacity-70"><X size={14} /></button>
        </div>
      )}

      {/* Draft-mode floating trigger + slide-out panel.
          Count includes both rate/restriction drafts (in-memory) AND unit-block
          drafts (server-side is_draft=true) so the user sees ONE unified batch. */}
      <DraftTrigger
        count={drafts.length + reservations.filter((r) => r.is_block && r.is_draft).length + Object.keys(moveDrafts).length}
        onOpen={() => setDraftPanelOpen(true)}
        onSend={sendDrafts}
        onDiscard={async () => {
          const rateCount = drafts.length;
          const blockCount = reservations.filter((r) => r.is_block && r.is_draft).length;
          const total = rateCount + blockCount + Object.keys(moveDrafts).length;
          if (!total) return;
          if (!window.confirm(`Poništiti sve nespremljene promjene? (${total})`)) return;
          clearDrafts();
          setMoveDrafts({});
          // Also delete server-side draft blocks
          const draftBlocks = reservations.filter((r) => r.is_block && r.is_draft);
          for (const b of draftBlocks) {
            try { await api.delete(`/unit-blocks/${b.id}`); } catch { /* keep going */ }
          }
          setReservations((rs) => rs.filter((r) => !(r.is_block && r.is_draft)));
          toast.success(`Poništeno ${total} promjena`);
        }}
        sending={sendingDrafts}
        hidden={draftPanelOpen}
      />
      <DraftPanel
        open={draftPanelOpen}
        drafts={drafts}
        blockDrafts={reservations.filter((r) => r.is_block && r.is_draft)}
        moveDrafts={moveDrafts}
        moveConflicts={moveConflicts}
        onRemoveMove={removeMoveDraft}
        units={units}
        onClose={() => setDraftPanelOpen(false)}
        onRemove={removeDraft}
        onRemoveBlock={async (blockId) => {
          try {
            await api.delete(`/unit-blocks/${blockId}`);
            setReservations((rs) => rs.filter((r) => r.id !== blockId));
          } catch (e) { toast.error("Greška pri brisanju blokade"); }
        }}
        onClear={clearDrafts}
        onSend={sendDrafts}
        sending={sendingDrafts}
      />
      {calGuestId && <GuestPopup guestId={calGuestId} onClose={() => setCalGuestId(null)} />}
      {moveConfirm && (
        <MoveDraftConfirm
          data={moveConfirm}
          units={units}
          onCancel={() => setMoveConfirm(null)}
          onConfirm={() => {
            addMoveDraft(moveConfirm.reservation, moveConfirm.target);
            moveConfirm.afterConfirm?.();
            setMoveConfirm(null);
            toast.success("Premještaj dodan u draft — spremi kad razriješiš sva preklapanja");
          }}
        />
      )}
    </div>
  );
}

function MoveDraftConfirm({ data, units, onCancel, onConfirm }) {
  const [ok, setOk] = useState(false);
  const { reservation: r, target, clash } = data;
  const from = units.find((u) => u.id === r.unit_id);
  const to = units.find((u) => u.id === target.unit_id);
  const typeChange = from && to && (from.room_type_id !== to.room_type_id || from.property_id !== to.property_id);
  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/45 sm:items-center sm:p-4" onClick={(e) => e.target === e.currentTarget && onCancel()} data-testid="move-draft-confirm">
      <div className="w-full max-w-md rounded-t-2xl border border-border bg-card p-5 shadow-2xl sm:rounded-xl">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700"><Warning size={18} weight="fill" /></span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground">Jedinica je zauzeta</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              <b className="text-foreground">{r.primary_guest_name || r.code}</b> → <b className="text-foreground">{to?.code || to?.name}</b> ({_fmtDateShort(target.check_in)} → {_fmtDateShort(target.check_out)})
              {clash ? <> preklapa se s <b className="text-foreground">{clash.primary_guest_name || clash.block_reason || clash.code}</b>.</> : " preklapa se s drugom rezervacijom."}
            </p>
          </div>
        </div>
        <ul className="mt-4 space-y-1.5 rounded-lg bg-muted/50 p-3 text-xs text-foreground">
          <li>• Premještaj ide u <b>draft</b>. Preklapanje je dopušteno samo dok je u draftu.</li>
          <li>• Spremanje neće biti moguće dok god su dvije rezervacije u istoj jedinici — premjesti i drugu rezervaciju (zamjena/rotacija).</li>
          {typeChange && <li className="text-amber-700">• Mijenja se tip smještaja ili objekt. Na Booking.com/Airbnb rezervaciji ostaje originalni tip i cijena.</li>}
        </ul>
        <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-foreground">
          <input type="checkbox" className="h-4 w-4" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="move-draft-understand" />
          Razumijem, privremeno križam rezervacije
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted" data-testid="move-draft-cancel">Odustani</button>
          <button type="button" disabled={!ok} onClick={onConfirm} className="rounded-md bg-amber-500 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-40" data-testid="move-draft-confirm-btn">Dodaj u draft</button>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// Rentlio-style inline cell editor (popover)
// =============================================================================
const FIELD_META = {
  channel_price: (channel) => ({
    title: (channel && channel !== "direct" ? (CHANNEL_LABELS[channel] || channel) : "Standard rate"),
    desc: (v) => `Postavi na ${v || 0} EUR`,
    boolean: false,
    supportsOperators: true,
    unit: "EUR",
  }),
  rate_plan_base_price: (ratePlanName) => ({
    title: ratePlanName ? `★ ${ratePlanName}` : "Best Available Rate",
    desc: (v) => `Postavi cijenu na ${v || 0} EUR`,
    boolean: false,
    supportsOperators: true,
    unit: "EUR",
  }),
  min_stay:         { title: "Min. boravak",       desc: (v) => `Na odabrane datume gost mora rezervirati najmanje ${v || "X"} noći`, boolean: false, min: 1, max: 365 },
  max_stay:         { title: "Max. boravak",       desc: (v) => `Na odabrane datume gost smije rezervirati najviše ${v || "X"} noći`, boolean: false, min: 1, max: 365 },
  min_advance_days: { title: "Min. za rezervaciju", desc: (v) => `Rezervacija mora biti napravljena najmanje ${v || "X"} dana unaprijed`, boolean: false, min: 0, max: 365 },
  max_advance_days: { title: "Max. za rezervaciju", desc: (v) => `Rezervacija se ne može napraviti više od ${v || "X"} dana unaprijed`, boolean: false, min: 0, max: 3650 },
  no_arrival:       { title: "Nema dolazaka",      desc: "Gosti mogu boraviti na te datume, ali ne mogu napraviti check-in.", boolean: true },
  no_departure:     { title: "Nema odlazaka",      desc: "Gosti mogu boraviti na te datume, ali ne mogu tada napraviti check-out iz objekta.", boolean: true },
  stop_sell:        { title: "Zaustavi prodaju",   desc: "Datumi nisu dostupni za rezerviranje ni preko jednog kanala.", boolean: true },
};

function CellEditor({ state, roomTypeOptions = [], onClose, onDraft }) {
  const meta = state.field === "channel_price"
    ? FIELD_META.channel_price(state.channel)
    : state.field === "rate_plan_base_price"
      ? FIELD_META.rate_plan_base_price(state.ratePlanName)
      : FIELD_META[state.field];
  const isPrice = state.field === "rate_plan_base_price" || state.field === "channel_price";
  const [value, setValue] = useState(() => {
    if (meta.boolean) return !!state.currentValue;
    const v = state.currentValue;
    return v == null ? "" : String(v);
  });
  const [startDate, setStartDate] = useState(state.date);
  const [endDate, setEndDate] = useState(state.endDate || state.date);
  const [selectedRt, setSelectedRt] = useState(() => (state.roomTypeId ? [state.roomTypeId] : []));
  const [rtOpen, setRtOpen] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => { inputRef.current?.focus?.(); inputRef.current?.select?.(); }, 60);
    return () => clearTimeout(t);
  }, []);

  const clickedPlan = useMemo(() => {
    const own = roomTypeOptions.find((o) => o.rt.id === state.roomTypeId);
    return own?.ratePlans.find((p) => p.id === state.ratePlanId) || null;
  }, [roomTypeOptions, state.roomTypeId, state.ratePlanId]);

  // Same plan on another room type: BAR ↔ BAR, otherwise match by code or name.
  const matchPlan = (opt) => {
    if (opt.rt.id === state.roomTypeId) return opt.ratePlans.find((p) => p.id === state.ratePlanId) || null;
    if (!clickedPlan) return null;
    if (clickedPlan.is_default) return opt.ratePlans.find((p) => p.is_default) || null;
    const norm = (x) => String(x || "").trim().toLowerCase();
    return opt.ratePlans.find((p) => (clickedPlan.code && norm(p.code) === norm(clickedPlan.code)) || norm(p.name) === norm(clickedPlan.name)) || null;
  };

  const applyOperator = (raw, current) => {
    const s = String(raw).trim();
    if (!s) return null;
    const m = s.match(/^([+\-])?\s*(\d+(?:[.,]\d+)?)\s*(%)?$/);
    if (!m) return null;
    const [, op, numStr, pct] = m;
    const n = parseFloat(numStr.replace(",", "."));
    if (!op) return n;
    const base = Number(current || 0);
    let delta = pct ? base * (n / 100) : n;
    if (op === "-") delta = -delta;
    return Math.round((base + delta) * 100) / 100;
  };

  const doSave = () => {
    setError("");
    if (!selectedRt.length) { setError("Odaberi barem jedan tip smještaja."); return; }
    if (endDate < startDate) { setError("Datum završetka je prije početka."); return; }
    let payloadValue = value;
    if (!meta.boolean) {
      if (meta.supportsOperators) {
        const relative = /^\s*[+\-]/.test(String(value));
        if (relative && selectedRt.length > 1) { setError("Za više tipova upiši točan iznos (bez + / -)."); return; }
        const resolved = applyOperator(value, state.currentValue);
        if (resolved == null) { setError("Nevaljan format (npr. 120, +10, -5%)"); return; }
        payloadValue = resolved;
      } else {
        const n = value === "" ? null : parseInt(value, 10);
        if (n !== null && (isNaN(n) || n < 0)) { setError("Broj mora biti 0 ili veći"); return; }
        payloadValue = n;
      }
    }
    const base = {
      propertyId: state.propertyId,
      field: state.field,
      channel: state.channel || null,
      startDate,
      endDate,
      newValue: payloadValue,
      fieldLabel: meta.title,
    };
    const drafts = [];
    const skipped = [];
    for (const opt of roomTypeOptions.filter((o) => selectedRt.includes(o.rt.id))) {
      const same = opt.rt.id === state.roomTypeId;
      const common = {
        ...base,
        roomTypeId: opt.rt.id,
        roomTypeName: opt.rt.name,
        unitIds: same ? (state.unitIds || opt.unitIds) : opt.unitIds,
        propertyId: opt.propertyId,
        propertyName: opt.propertyName,
        previousValue: same ? state.currentValue : null,
      };
      if (isPrice) {
        const plan = state.ratePlanId ? matchPlan(opt) : null;
        if (state.ratePlanId && !plan) { skipped.push(opt.rt.name); continue; }
        drafts.push({ ...common, ratePlanId: plan?.id || null, ratePlanName: plan?.name || null });
      } else {
        // Restrictions apply to every rate plan of the room type.
        const plans = opt.ratePlans.length ? opt.ratePlans : [null];
        for (const plan of plans) drafts.push({ ...common, ratePlanId: plan?.id || null, ratePlanName: plan?.name || null });
      }
    }
    if (!drafts.length) { setError("Odabrani tipovi nemaju odgovarajući rate plan."); return; }
    if (skipped.length) toast.info(`Preskočeno (nema rate plana "${clickedPlan?.name}"): ${skipped.join(", ")}`);
    onDraft?.(drafts);
  };

  const days = Math.max(1, Math.round((new Date(`${endDate}T00:00:00`) - new Date(`${startDate}T00:00:00`)) / 86400000) + 1);
  const grouped = useMemo(() => {
    const m = new Map();
    for (const o of roomTypeOptions) {
      if (!m.has(o.propertyId)) m.set(o.propertyId, { name: o.propertyName, items: [] });
      m.get(o.propertyId).items.push(o);
    }
    return Array.from(m.entries());
  }, [roomTypeOptions]);
  const toggleRt = (id) => setSelectedRt((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const togglePropRts = (items) => {
    const ids = items.map((o) => o.rt.id);
    const all = ids.every((id) => selectedRt.includes(id));
    setSelectedRt((s) => (all ? s.filter((x) => !ids.includes(x)) : Array.from(new Set([...s, ...ids]))));
  };
  const rtSummary = (() => {
    const names = roomTypeOptions.filter((o) => selectedRt.includes(o.rt.id)).map((o) => o.rt.name);
    if (!names.length) return "Nijedan tip";
    return names.length <= 2 ? names.join(", ") : `${names[0]} +${names.length - 1}`;
  })();
  const scopeHint = isPrice
    ? (clickedPlan?.is_default ? "BAR mijenja i povezane rate planove" : clickedPlan ? `Mijenja samo "${clickedPlan.name}"` : "")
    : "Primjenjuje se na sve rate planove";

  return (
    <ResponsiveSheet
      onClose={onClose}
      title={meta.title}
      subtitle={state.propertyName}
      width={460}
      testId="cell-editor"
      footer={
        <>
          <span className="mr-auto min-w-0 truncate text-xs text-muted-foreground" data-testid="cell-editor-summary">
            {selectedRt.length} {selectedRt.length === 1 ? "tip" : "tipa"} · {days} {days === 1 ? "dan" : "dana"}
          </span>
          <button type="button" onClick={onClose} className="hidden h-10 shrink-0 rounded-md border border-border px-4 text-sm font-medium hover:bg-muted sm:inline-flex sm:items-center" data-testid="cell-editor-close">Odustani</button>
          <button type="button" onClick={doSave} className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md bg-emerald-600 px-4 text-sm font-semibold text-white hover:bg-emerald-700" data-testid="cell-editor-save">
            <Check size={16} weight="bold" /> Dodaj u draft
          </button>
        </>
      }
    >
      <div className="space-y-4 p-4 sm:p-5">
        {meta.boolean ? (
          <div className="grid grid-cols-2 gap-2" data-testid="cell-editor-toggle">
            {[{ v: true, l: "Da", id: "yes" }, { v: false, l: "Ne", id: "no" }].map((o) => (
              <button key={o.id} type="button" onClick={() => setValue(o.v)}
                className={`h-11 rounded-md border text-sm font-semibold transition-colors ${value === o.v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:bg-muted"}`}
                data-testid={`cell-editor-${o.id}`}>{o.l}</button>
            ))}
          </div>
        ) : (
          <div className="relative">
            <input
              ref={inputRef}
              type={meta.supportsOperators ? "text" : "number"}
              inputMode={meta.supportsOperators ? "decimal" : "numeric"}
              min={meta.min} max={meta.max}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doSave(); } }}
              className="h-12 w-full rounded-md border-2 border-primary bg-background px-4 text-center font-mono text-xl font-semibold text-foreground focus:outline-none"
              placeholder={meta.supportsOperators ? "120" : ""}
              data-testid="cell-editor-input"
            />
            {meta.unit && <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">{meta.unit}</span>}
          </div>
        )}
        <p className="-mt-2 truncate text-center text-xs text-muted-foreground" title={typeof meta.desc === "function" ? meta.desc(value) : meta.desc}>
          {meta.supportsOperators ? "Iznos ili +10, -5%" : (typeof meta.desc === "function" ? meta.desc(value) : meta.desc)}
        </p>
        {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-center text-xs text-destructive" data-testid="cell-editor-error">{error}</p>}

        <div className="grid grid-cols-2 gap-3">
          <label className="block min-w-0">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Od</span>
            <DatePicker value={startDate} onChange={(v) => { if (!v) return; setStartDate(v); if (endDate < v) setEndDate(v); }} minDate={todayIsoLocal()} clearable={false} className="h-10 w-full" data-testid="cell-editor-start" />
          </label>
          <label className="block min-w-0">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Do (uključivo)</span>
            <DatePicker value={endDate} onChange={(v) => v && setEndDate(v)} minDate={startDate} clearable={false} className="h-10 w-full" data-testid="cell-editor-end" />
          </label>
        </div>

        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Tipovi smještaja</span>
          <button type="button" onClick={() => setRtOpen((v) => !v)}
            className={`flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-md border px-3 text-sm ${rtOpen ? "border-primary" : "border-border"} bg-background text-foreground`}
            aria-expanded={rtOpen} data-testid="cell-editor-rt-toggle">
            <span className="truncate">{rtSummary}</span>
            <span className="flex shrink-0 items-center gap-1.5">
              {selectedRt.length > 1 && <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary-foreground">{selectedRt.length}</span>}
              <CaretDown size={12} weight="bold" className={`opacity-60 transition-transform ${rtOpen ? "rotate-180" : ""}`} />
            </span>
          </button>
          {rtOpen && (
            <div className="mt-1 max-h-60 overflow-y-auto rounded-md border border-border bg-background p-1" data-testid="cell-editor-rt-menu">
              {grouped.map(([pid, g]) => {
                const allOn = g.items.every((o) => selectedRt.includes(o.rt.id));
                return (
                  <div key={pid} className="py-0.5">
                    {grouped.length > 1 && (
                      <button type="button" onClick={() => togglePropRts(g.items)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[11px] font-bold uppercase tracking-wide text-muted-foreground hover:bg-muted" data-testid={`cell-editor-prop-${pid}`}>
                        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${allOn ? "border-primary bg-primary text-primary-foreground" : "border-foreground/40 bg-card"}`}>{allOn && <Check size={10} weight="bold" />}</span>
                        <span className="truncate">{g.name}</span>
                      </button>
                    )}
                    {g.items.map((o) => {
                      const on = selectedRt.includes(o.rt.id);
                      const noPlan = isPrice && state.ratePlanId && !matchPlan(o);
                      return (
                        <button key={o.rt.id} type="button" onClick={() => toggleRt(o.rt.id)} disabled={noPlan}
                          className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 ${grouped.length > 1 ? "pl-6" : ""}`}
                          title={noPlan ? `Nema rate plana "${clickedPlan?.name}"` : undefined}
                          data-testid={`cell-editor-rt-${o.rt.id}`}>
                          <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? "border-primary bg-primary text-primary-foreground" : "border-foreground/40 bg-card"}`}>{on && <Check size={10} weight="bold" />}</span>
                          <span className="min-w-0 flex-1 truncate">{o.rt.name}</span>
                          <span className="shrink-0 text-[11px] text-muted-foreground">{o.unitIds.length} jed.</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
          {scopeHint && <p className="mt-1 truncate text-[11px] text-muted-foreground">{scopeHint}</p>}
        </div>

      </div>
    </ResponsiveSheet>
  );
}

function BlockDialog({ groups, onClose, onCreated }) {
  const today = todayIsoLocal();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleGroup = (ids) => {
    const all = ids.every((id) => selected.includes(id));
    setSelected((s) => (all ? s.filter((x) => !ids.includes(x)) : Array.from(new Set([...s, ...ids]))));
  };
  const nights = Math.max(1, Math.round((new Date(`${endDate}T00:00:00`) - new Date(`${startDate}T00:00:00`)) / 86400000) + 1);
  const save = async () => {
    if (!selected.length) { setError("Odaberi barem jednu jedinicu."); return; }
    setSaving(true); setError("");
    const d = new Date(`${endDate}T00:00:00`); d.setDate(d.getDate() + 1);
    const checkOut = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const created = [];
    for (const unitId of selected) {
      try {
        const { data } = await api.post("/unit-blocks", { unit_id: unitId, check_in: startDate, check_out: checkOut, block_reason: reason.trim() || "Blokada", is_draft: true });
        created.push(data);
      } catch (e) { toast.error(formatApiError(e)); }
    }
    setSaving(false);
    if (created.length) {
      toast.success(`${created.length} ${created.length === 1 ? "blokada dodana" : "blokade dodane"} u draft · pošalji kad završiš`);
      onCreated(created);
    }
  };
  const box = (on) => `flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? "border-slate-700 bg-slate-700 text-white" : "border-foreground/40 bg-card"}`;
  return (
    <ResponsiveSheet
      onClose={onClose}
      title={<span className="inline-flex items-center gap-2"><Lock size={16} weight="bold" /> Nova blokada</span>}
      subtitle="Jedinice nisu dostupne za prodaju"
      width={460}
      testId="block-dialog"
      footer={
        <>
          <span className="mr-auto min-w-0 truncate text-xs text-muted-foreground" data-testid="block-dialog-summary">
            {selected.length} {selected.length % 10 >= 2 && selected.length % 10 <= 4 && (selected.length % 100 < 12 || selected.length % 100 > 14) ? "jedinice" : "jedinica"} · {nights} {nights === 1 ? "noć" : "noći"}
          </span>
          <button type="button" onClick={onClose} className="hidden h-10 shrink-0 rounded-md border border-border px-4 text-sm font-medium hover:bg-muted sm:inline-flex sm:items-center" data-testid="block-dialog-cancel">Odustani</button>
          <button type="button" onClick={save} disabled={saving} className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md bg-slate-700 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50" data-testid="block-dialog-save">
            {saving ? <CircleNotch size={16} className="animate-spin" /> : <Check size={16} weight="bold" />} Dodaj u draft
          </button>
        </>
      }
    >
      <div className="space-y-4 p-4 sm:p-5">
        {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-center text-xs text-destructive" data-testid="block-dialog-error">{error}</p>}
        <div className="grid grid-cols-2 gap-3">
          <label className="block min-w-0">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Od</span>
            <DatePicker value={startDate} onChange={(v) => { if (!v) return; setStartDate(v); if (endDate < v) setEndDate(v); }} minDate={today} clearable={false} className="h-10 w-full" data-testid="block-dialog-start" />
          </label>
          <label className="block min-w-0">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Do (uključivo)</span>
            <DatePicker value={endDate} onChange={(v) => v && setEndDate(v)} minDate={startDate} clearable={false} className="h-10 w-full" data-testid="block-dialog-end" />
          </label>
        </div>
        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Jedinice</span>
          <div className="max-h-64 overflow-y-auto rounded-md border border-foreground/20 bg-card p-1" data-testid="block-dialog-units">
            {groups.map((g) => {
              const ids = g.units.filter((u) => u.active !== false).map((u) => u.id);
              const allOn = ids.length > 0 && ids.every((id) => selected.includes(id));
              return (
                <div key={g.property.id} className="py-0.5">
                  <button type="button" onClick={() => toggleGroup(ids)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[11px] font-bold uppercase tracking-wide text-muted-foreground hover:bg-muted" data-testid={`block-dialog-prop-${g.property.id}`}>
                    <span className={box(allOn)}>{allOn && <Check size={10} weight="bold" />}</span>
                    <span className="truncate">{g.property.name}</span>
                  </button>
                  {g.units.filter((u) => u.active !== false).map((u) => {
                    const on = selected.includes(u.id);
                    return (
                      <button key={u.id} type="button" onClick={() => toggle(u.id)} className="flex w-full items-center gap-2 rounded py-2 pl-6 pr-2 text-left text-sm hover:bg-muted" data-testid={`block-dialog-unit-${u.id}`}>
                        <span className={box(on)}>{on && <Check size={10} weight="bold" />}</span>
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: u.color }} />
                        <span className="min-w-0 flex-1 truncate">{u.code}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Napomena (opcionalno)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={120} placeholder="npr. Renovacija, vlasnik koristi…" className="rw-control" data-testid="block-dialog-reason" />
        </label>
      </div>
    </ResponsiveSheet>
  );
}

function todayIsoLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function QuickReservationPreview({ reservation, anchorRect, onClose, onOpenFull, onCheckIn, onTogglePaid, onOpenGuest }) {
  const r = reservation;
  // Local state for note + task quick-edit (mirrors + persists back to backend)
  const [note, setNote] = useState(r?.notes || "");
  const [tasks, setTasks] = useState(r?.tasks || []);
  const [newTask, setNewTask] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // F8.11.1: local check-in state so popover reflects status change without a full reload
  const [localCheckinStatus, setLocalCheckinStatus] = useState(null);
  const headRef = useRef(null);
  const bodyRef = useRef(null);
  const footRef = useRef(null);
  const [naturalH, setNaturalH] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      if (!bodyRef.current) return;
      setNaturalH((headRef.current?.offsetHeight || 0) + bodyRef.current.scrollHeight + (footRef.current?.offsetHeight || 0) + 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (bodyRef.current) Array.from(bodyRef.current.children).forEach((c) => ro.observe(c));
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [reservation?.id]);
  const { panelRef: qpPanelRef, backdropRef: qpBackdropRef, requestClose: qpClose } = useSheetDrag({ onClose });
  const [chatOpen, setChatOpen] = useState(false);

  if (!reservation) return null;
  const effectiveStatus = localCheckinStatus || r.status;
  const isCheckedIn = effectiveStatus === "checked_in";
  const isPaid = r.payment_status === "paid" || r.payment_status === "refunded";
  const nights = r.nights || 0;

  const persist = async (patch) => {
    try {
      const { data } = await api.put(`/reservations/${r.id}`, patch);
      // sync back so parent list shows latest indicator bars
      window.dispatchEvent(new CustomEvent("rw-reservation-updated", { detail: data }));
    } catch (e) {
      toast.error("Greška pri spremanju");
    }
  };

  const saveNote = async () => {
    setSavingNote(true);
    await persist({ notes: note });
    setSavingNote(false);
    toast.success("Napomena spremljena");
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    const next = [...tasks, { id: `t${Date.now()}`, title: newTask.trim(), done: false, created_at: new Date().toISOString() }];
    setTasks(next);
    setNewTask("");
    await persist({ tasks: next });
  };

  const toggleTask = async (id) => {
    const next = tasks.map((t) => (t.id === id ? { ...t, done: !t.done } : t));
    setTasks(next);
    await persist({ tasks: next });
  };

  const removeTask = async (id) => {
    const next = tasks.filter((t) => t.id !== id);
    setTasks(next);
    await persist({ tasks: next });
  };

  // Popup sizing — wide 2-column card on desktop, positioned so the WHOLE card fits the viewport (no inner scroll)
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const isMobile = vw < 900;
  const isSheet = vw < 640;
  const POP_W = isMobile ? Math.min(vw - 16, 560) : Math.min(vw - 32, 820);
  const gap = 8;
  const MAX_H = vh - 24;
  const h = Math.min(naturalH || 520, MAX_H);
  let left, top;
  if (isMobile) {
    left = Math.max(8, (vw - POP_W) / 2);
    top = Math.max(12, (vh - h) / 2);
  } else {
    left = anchorRect.left + anchorRect.width + gap;
    if (left + POP_W > vw - 16) left = anchorRect.left - POP_W - gap;
    if (left < 16) left = Math.max(16, (vw - POP_W) / 2);
    top = Math.min(Math.max(12, anchorRect.top - 40), vh - h - 12);
    top = Math.max(12, top);
  }

  return createPortal(
    <div ref={qpBackdropRef} className={`fixed inset-0 z-[60] ${isSheet ? "rw-sheet-backdrop bg-black/45" : ""}`} data-testid="reservation-quick-preview" onClick={qpClose}>
      <div
        ref={qpPanelRef}
        className={`absolute flex flex-col overflow-hidden border border-border bg-card shadow-2xl ${isSheet ? "rw-sheet-panel rounded-t-2xl" : "rw-pop rounded-lg"}`}
        style={isSheet ? { left: 0, right: 0, bottom: 0, maxHeight: "92dvh" } : { left, top, width: POP_W, maxHeight: MAX_H }}
        onClick={(e) => e.stopPropagation()}
      >
        {isSheet && <div className="flex shrink-0 justify-center pb-1 pt-2" data-testid="quick-preview-handle"><div className="h-1 w-10 rounded-full bg-muted-foreground/30" /></div>}
        {/* Header: code + status + close */}
        <div ref={headRef} className={`flex items-center justify-between border-b border-border px-4 py-2.5 ${isSheet ? "" : "rounded-t-lg bg-muted/40"}`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-mono font-semibold text-muted-foreground">{r.code}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
              isCheckedIn ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"
            }`}>
              {(STATUS_LABELS[r.status] || r.status)}
            </span>
          </div>
          <button onClick={qpClose} className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="quick-preview-close">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div ref={bodyRef} data-sheet-scroll className={`flex-1 overflow-y-auto p-4 ${isMobile ? "space-y-4" : "grid grid-cols-2 items-start gap-5"}`}>
          <div className="min-w-0 space-y-4" data-testid="quick-preview-col-main">
          {/* Guest name + Profil button (larger) */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-lg font-semibold text-foreground truncate" data-testid="quick-preview-guest-name">
                {r.primary_guest_name || "Bez imena"}
              </p>
              {r.primary_guest_email && (
                <p className="text-sm text-muted-foreground truncate">{r.primary_guest_email}</p>
              )}
            </div>
            {r.primary_guest_id && (
              <button
                type="button"
                onClick={() => onOpenGuest?.(r.primary_guest_id)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                data-testid="quick-preview-open-guest-profile"
                title="Otvori profil gosta"
              >
                <User size={13} weight="bold" /> Profil
              </button>
            )}
          </div>

          {/* Two rows of 3 columns: Prijava | Odjava | Gosti / Noći | Ukupno | Izvor */}
          <div className="grid grid-cols-3 gap-x-4 gap-y-3 text-sm">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Prijava</p>
              <p className="mt-0.5 font-semibold text-foreground">{fmtDatePreview(r.check_in)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Odjava</p>
              <p className="mt-0.5 font-semibold text-foreground">{fmtDatePreview(r.check_out)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Gosti</p>
              <p className="mt-0.5 font-semibold text-foreground">
                {r.adults || 1} odraslih{r.children ? `, ${r.children} djece` : ""}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Noći</p>
              <p className="mt-0.5 font-semibold text-foreground">{nights}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Ukupno</p>
              <p className="mt-0.5 font-mono font-semibold text-foreground">
                {Number(r.total_amount || 0).toFixed(2)} {r.currency}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Izvor</p>
              <p className="mt-0.5 font-semibold text-foreground truncate" title={SOURCE_LABELS[r.source] || r.source}>
                {SOURCE_LABELS[r.source] || r.source || "—"}
              </p>
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-2">
            <label className="flex cursor-pointer items-center justify-between rounded-md border border-border px-3 py-2">
              <span className="text-sm font-medium text-foreground">Plaćeno</span>
              <button
                type="button"
                onClick={() => onTogglePaid?.(!isPaid)}
                className={`relative inline-block h-5 w-9 rounded-full transition ${isPaid ? "bg-emerald-500" : "bg-slate-300"}`}
                data-testid="quick-preview-paid-toggle"
              >
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${isPaid ? "left-4" : "left-0.5"}`} />
              </button>
            </label>

            {r.status !== "cancelled" && r.status !== "no_show" && (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <span className="text-sm font-medium text-foreground">
                  {isCheckedIn ? "Checked-in" : "Check-in"}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    if (!isCheckedIn) { if (await onCheckIn?.(false)) setLocalCheckinStatus("checked_in"); }
                    else if (window.confirm("Poništiti check-in? Rezervacija se vraća u status 'Potvrđena'.")) { if (await onCheckIn?.(true)) setLocalCheckinStatus("confirmed"); }
                  }}
                  title={isCheckedIn ? "Klikni za poništavanje check-ina" : "Pokreni check-in"}
                  className={`relative inline-block h-5 w-9 cursor-pointer rounded-full transition ${isCheckedIn ? "bg-emerald-500 hover:bg-emerald-600" : "bg-slate-300 hover:bg-slate-400"}`}
                  data-testid="quick-preview-checkin-toggle"
                >
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${isCheckedIn ? "left-4" : "left-0.5"}`} />
                </button>
              </div>
            )}
          </div>

          <CheckinSection
            reservationId={r.id}
            initialStatus={r.checkin_status || r.status}
            onUpdated={(patch) => {
              if (patch?.status) setLocalCheckinStatus(patch.status);
              window.dispatchEvent(new CustomEvent("rw-reservation-updated", { detail: { ...r, ...patch } }));
            }}
          />
          </div>

          <div className="min-w-0 space-y-4" data-testid="quick-preview-col-side">
          {/* Interna napomena — editable inline */}
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Interna napomena</p>
            <textarea
              rows={isMobile ? 4 : 7}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Dodaj napomenu..."
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="quick-preview-note"
            />
          </div>

          {/* Zadaci — add + toggle + delete */}
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              Zadaci {tasks.filter((t) => !t.done).length > 0 && <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[9px] font-bold text-white">{tasks.filter((t) => !t.done).length}</span>}
            </p>
            <ul className="mb-2 space-y-1">
              {tasks.map((t) => (
                <li key={t.id} className="flex items-center gap-2 text-sm" data-testid={`quick-preview-task-${t.id}`}>
                  <input type="checkbox" checked={!!t.done} onChange={() => toggleTask(t.id)} className="h-4 w-4 rounded border-border" />
                  <span className={`flex-1 ${t.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{t.title}</span>
                  <button type="button" onClick={() => removeTask(t.id)} className="rounded p-0.5 text-muted-foreground hover:text-rose-500">
                    <Trash size={12} />
                  </button>
                </li>
              ))}
              {tasks.length === 0 && <li className="text-xs italic text-muted-foreground">Nema zadataka.</li>}
            </ul>
            <div className="flex gap-2">
              <input
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTask()}
                placeholder="Novi zadatak..."
                className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                data-testid="quick-preview-new-task"
              />
              <button
                type="button"
                onClick={addTask}
                disabled={!newTask.trim()}
                className="rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                data-testid="quick-preview-add-task"
              >
                <Plus size={14} weight="bold" />
              </button>
            </div>
          </div>

          </div>
        </div>

        {/* Footer buttons with extra padding below */}
        <div ref={footRef} className="flex items-center gap-2 border-t border-border bg-card px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            onClick={onOpenFull}
            title="Detalji rezervacije"
            className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground hover:bg-muted"
            data-testid="quick-preview-open-full"
          >
            <List size={16} /> <span className={isSheet ? "" : ""}>Detalji</span>
          </button>
          <button
            onClick={() => setChatOpen(true)}
            title="Inbox gosta"
            className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground hover:bg-muted"
            data-testid="quick-preview-open-inbox"
          >
            <ChatCircleText size={16} /> <span>Inbox</span>
          </button>
          <button
            onClick={async () => {
              try {
                await persist({ notes: note, tasks });
                toast.success("Promjene spremljene");
                onClose?.();
              } catch { /* toast handled inside persist */ }
            }}
            className="inline-flex h-10 min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            data-testid="quick-preview-save"
          >
            <Check size={16} weight="bold" /> Spremi
          </button>
        </div>
      </div>
      {chatOpen && <OpenChatDialog reservation={r} onClose={() => setChatOpen(false)} />}
    </div>,
    document.body,
  );
}

export function OpenChatDialog({ reservation, onClose }) {
  const [state, setState] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    let alive = true;
    api.post(`/inbox/open-for-reservation/${reservation.id}`)
      .then(({ data }) => {
        if (!alive) return;
        if (data.thread_id) window.location.assign(`/inbox?thread=${data.thread_id}`);
        else setState(data);
      })
      .catch((e) => { if (alive) setState({ error: formatApiError(e) }); });
    return () => { alive = false; };
  }, [reservation.id]);
  const start = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/inbox/start/${reservation.id}`, { message: msg });
      toast.success("Poruka poslana gostu");
      if (data.thread_id) window.location.assign(`/inbox?thread=${data.thread_id}`);
      else onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/45 sm:items-center sm:p-4" onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }} data-testid="open-chat-dialog">
      <div className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-border bg-card p-5 shadow-2xl sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-semibold text-foreground"><ChatCircleText size={18} className="text-primary" /> Razgovor s gostom</h3>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted" data-testid="open-chat-close"><X size={16} /></button>
        </div>
        {!state && <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><CircleNotch size={16} className="animate-spin" /> Tražim razgovor…</div>}
        {state?.error && <p className="text-sm text-destructive">{state.error}</p>}
        {state && !state.error && (
          <div className="space-y-4">
            <p className="rounded-md bg-muted/60 p-3 text-xs text-foreground" data-testid="open-chat-reason">{state.reason}</p>
            {state.can_start && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground">Započni razgovor porukom</label>
                <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} className="rw-input w-full text-sm" placeholder={`Pozdrav ${reservation.primary_guest_name?.split(" ")[0] || ""}, …`} data-testid="open-chat-message" />
                <button type="button" onClick={start} disabled={!msg.trim() || sending} className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="open-chat-send">
                  {sending ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />} Pošalji i otvori razgovor
                </button>
              </div>
            )}
            <div>
              <p className="mb-1 text-xs font-semibold text-foreground">Ili pošalji automatsku poruku ručno</p>
              <TemplateDiagnose reservationId={reservation.id} autoLoad />
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function fmtDatePreview(iso) {
  if (!iso || iso.length < 10) return iso || "";
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

// Legend chip — shows what each color bar on a reservation block means.
export function CalendarLegend() {
  const items = [
    { c: "bg-rose-500", l: "Neplaćeno" },
    { c: "bg-amber-500", l: "Check-in u tijeku" },
    { c: "bg-sky-500", l: "Napomena" },
    { c: "bg-violet-500", l: "Zadaci" },
  ];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-muted-foreground hover:bg-muted"
          data-testid="calendar-legend-trigger"
        >
          <Tag size={14} /> Oznake
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2" data-testid="calendar-legend">
        <div className="space-y-1.5 text-xs text-foreground">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className={`h-1.5 w-6 shrink-0 rounded-sm ${it.c}`} /> {it.l}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}


// -----------------------------------------------------------------------------
// Draft Mode UI — floating trigger + right-side slide-out panel with pending
// changes. When user clicks "Pošalji u Channex" ALL changes are shipped
// via POST /rate-periods/batch-apply as ONE atomic request → ONE Channex
// API call → ONE certification task ID. No debounce timing games.
// -----------------------------------------------------------------------------
function DraftTrigger({ count, onOpen, onSend, onDiscard, sending, hidden }) {
  if (hidden || count <= 0) return null;
  return (
    <div className="rw-sheet-panel fixed bottom-20 right-3 z-40 flex items-center gap-1 rounded-full border border-border bg-card p-1 shadow-xl lg:bottom-5 lg:right-5" data-testid="draft-bar">
      <button
        type="button"
        onClick={onDiscard}
        disabled={sending}
        className="flex h-9 w-9 items-center justify-center rounded-full text-rose-600 transition-colors hover:bg-rose-50 disabled:opacity-50"
        title="Poništi sve nespremljene promjene"
        aria-label="Poništi promjene"
        data-testid="draft-discard-quick"
      >
        <X size={16} weight="bold" />
      </button>
      <button
        type="button"
        onClick={onOpen}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted"
        title={`${count} nespremljenih promjena — pregled`}
        aria-label="Pregled drafta"
        data-testid="draft-trigger"
      >
        <FileText size={17} />
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-emerald-600 px-1 text-[9px] font-bold text-white" data-testid="draft-count">{count}</span>
      </button>
      <button
        type="button"
        onClick={onSend}
        disabled={sending}
        className="flex h-9 items-center gap-1.5 rounded-full bg-emerald-600 px-4 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
        title="Objavi sve promjene odjednom"
        data-testid="draft-send-quick"
      >
        {sending ? <CircleNotch size={14} className="animate-spin" /> : <Check size={14} weight="bold" />}
        {sending ? "Šaljem" : "Spremi"}
      </button>
    </div>
  );
}

const DRAFT_FIELD_LABEL = {
  base_price: "Cijena",
  channel_price: "Cijena po kanalu",
  rate_plan_base_price: "Cijena",
  min_stay: "Min. boravak",
  max_stay: "Max. boravak",
  min_advance_days: "Min. dana unaprijed",
  max_advance_days: "Max. dana unaprijed",
  no_arrival: "Nema dolazaka",
  no_departure: "Nema odlazaka",
  stop_sell: "Zaustavi prodaju",
};

function _fmtDateShort(iso) {
  if (!iso || iso.length < 10) return iso || "";
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

function _formatDraftValue(field, val) {
  if (val === true) return "Da";
  if (val === false) return "Ne";
  if (val === null || val === undefined || val === "") return "—";
  if (field === "base_price" || field === "rate_plan_base_price" || field === "channel_price") {
    return `${Number(val).toFixed(2)} €`;
  }
  return String(val);
}

function DraftPanel({ open, drafts, blockDrafts = [], moveDrafts = {}, moveConflicts = new Set(), onRemoveMove, units = [], onClose, onRemove, onRemoveBlock, onClear, onSend, sending }) {
  if (!open) return null;
  const moves = Object.entries(moveDrafts);
  const count = drafts.length + blockDrafts.length + moves.length;
  const uCode = (id) => { const u = units.find((x) => x.id === id); return u?.code || u?.name || "?"; };
  return (
    <>
      {/* backdrop */}
      <button
        type="button"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]"
        aria-label="Zatvori draft panel"
        data-testid="draft-panel-backdrop"
      />
      {/* panel */}
      <aside
        className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full max-w-md flex-col border-l border-border bg-card shadow-2xl"
        data-testid="draft-panel"
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Nespremljene promjene</p>
            <h2 className="text-lg font-semibold text-foreground" data-testid="draft-panel-count">
              {count} {count === 1 ? "promjena" : "promjena"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Zatvori"
            data-testid="draft-panel-close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {count === 0 ? (
            <div className="mt-16 text-center">
              <p className="text-sm text-muted-foreground">Nema nespremljenih promjena.</p>
              <p className="mt-2 text-xs text-muted-foreground/70">Klikni bilo koju ćeliju u kalendaru za dodavanje promjene u draft.</p>
            </div>
          ) : (
            <>
              {moves.length > 0 && (
                <div className="mb-4" data-testid="draft-moves">
                  <p className="mb-1.5 px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Premještaji rezervacija ({moves.length})</p>
                  {moveConflicts.size > 0 && (
                    <div className="mb-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="draft-moves-conflict">
                      <b>Preklapanje!</b> Dvije rezervacije su u istoj jedinici — spremanje je blokirano dok to ne razriješiš.
                    </div>
                  )}
                  <ul className="space-y-2">
                    {moves.map(([id, d]) => (
                      <li key={id} className={`group flex items-start gap-2 rounded-lg border px-3 py-2 shadow-sm ${moveConflicts.has(id) ? "border-rose-300 bg-rose-50/60" : "border-amber-300 bg-amber-50/60"}`} data-testid={`draft-move-${id}`}>
                        <div className="min-w-0 flex-1 text-xs">
                          <p className="truncate text-sm font-semibold text-foreground">{d.guest || d.code}</p>
                          <p className="text-muted-foreground">
                            <span className="font-mono">{uCode(d.orig.unit_id)}</span> {_fmtDateShort(d.orig.check_in)}–{_fmtDateShort(d.orig.check_out)}
                            {" → "}<span className="font-mono font-semibold text-foreground">{uCode(d.unit_id)}</span> {_fmtDateShort(d.check_in)}–{_fmtDateShort(d.check_out)}
                          </p>
                          {moveConflicts.has(id) && <p className="mt-0.5 font-semibold text-rose-700">Preklapa se u {uCode(d.unit_id)}</p>}
                        </div>
                        <button type="button" onClick={() => onRemoveMove?.(id)} className="mt-1 shrink-0 rounded-full p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" title="Vrati na originalno mjesto" data-testid={`draft-move-remove-${id}`}>
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {drafts.length > 0 && (
                <>
                  <p className="mb-1.5 px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Cijene i restrikcije ({drafts.length})</p>
                  <ul className="space-y-2">
                    {drafts.map((d) => {
                      const label = d.fieldLabel || DRAFT_FIELD_LABEL[d.field] || d.field;
                      const dateStr = d.startDate === d.endDate
                        ? _fmtDateShort(d.startDate)
                        : `${_fmtDateShort(d.startDate)} → ${_fmtDateShort(d.endDate)}`;
                      const scope = [
                        d.roomTypeName,
                        d.ratePlanName ? `★ ${d.ratePlanName}` : null,
                        d.channel && d.channel !== "direct" ? `kanal: ${d.channel}` : null,
                      ].filter(Boolean).join(" · ");
                      return (
                        <li
                          key={d.id}
                          className="group flex items-start gap-2 rounded-lg border border-border bg-background px-3 py-2 shadow-sm"
                          data-testid={`draft-item-${d.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono font-semibold text-primary">{dateStr}</span>
                              <span className="truncate">{scope}</span>
                            </div>
                            <div className="mt-1 flex items-baseline gap-2">
                              <span className="text-xs font-medium text-muted-foreground">{label}:</span>
                              <span className="text-sm font-bold text-foreground">{_formatDraftValue(d.field, d.newValue)}</span>
                              {d.previousValue !== undefined && d.previousValue !== null && d.previousValue !== "" && (
                                <span className="text-xs text-muted-foreground line-through">{_formatDraftValue(d.field, d.previousValue)}</span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => onRemove(d.id)}
                            className="mt-1 shrink-0 rounded-full p-1 text-muted-foreground opacity-0 hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                            title="Ukloni ovu promjenu iz drafta"
                            data-testid={`draft-remove-${d.id}`}
                          >
                            <X size={14} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {blockDrafts.length > 0 && (
                <>
                  <p className={`mb-1.5 px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground ${drafts.length > 0 ? "mt-4" : ""}`}>Blokade jedinica ({blockDrafts.length})</p>
                  <ul className="space-y-2">
                    {blockDrafts.map((b) => {
                      const unit = units.find((u) => u.id === b.unit_id);
                      return (
                        <li
                          key={b.id}
                          className="group flex items-start gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-900/40"
                          data-testid={`draft-block-${b.id}`}
                        >
                          <Lock size={13} weight="bold" className="mt-0.5 shrink-0 text-slate-500" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span className="rounded bg-slate-500/10 px-1.5 py-0.5 font-mono font-semibold text-slate-700 dark:text-slate-300">{_fmtDateShort(b.check_in)} → {_fmtDateShort(b.check_out)}</span>
                              <span className="truncate font-mono">{unit?.code || b.unit_id}</span>
                            </div>
                            <div className="mt-1 text-sm font-medium text-foreground">
                              {b.block_reason || "Blokada jedinice"}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => onRemoveBlock && onRemoveBlock(b.id)}
                            className="mt-1 shrink-0 rounded-full p-1 text-muted-foreground opacity-0 hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                            title="Ukloni blokadu"
                            data-testid={`draft-block-remove-${b.id}`}
                          >
                            <X size={14} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </>
          )}
        </div>

        <footer className="border-t border-border px-4 py-3 mb-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClear}
              disabled={count === 0 || sending}
              className="rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
              data-testid="draft-clear-all"
            >
              Očisti cijene
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={count === 0 || sending || moveConflicts.size > 0}
              title={moveConflicts.size > 0 ? "Razriješi preklapanje rezervacija prije spremanja" : undefined}
              className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
              data-testid="draft-send"
            >
              {sending
                ? <><CircleNotch size={14} className="inline animate-spin" /> Šaljem…</>
                : <>Spremi promjene  ({count})</>}
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

